import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import API, { BACKEND_URL } from "@/lib/api";
import DOMPurify from "dompurify";
import { useTheme } from "@/lib/theme";
import { useBranding } from "@/lib/branding";
import { useCurrency, CURRENCIES } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CellInput } from "@/components/ui/cell-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Save, Plus, Trash2, Sun, Moon, Palette, Edit2, Check, X, Eye, Upload, ImageIcon, UserPlus, Mail, Send, Shield, AlertCircle, CheckCircle2, XCircle, RefreshCw, Server, BarChart3 as BarChart3Icon, TrendingUp as TrendingUpIcon, Clock as ClockIcon, Search as SearchIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from "lucide-react";

const DEFAULT_ROLES = ["Director", "Associate", "Senior Engineer", "Engineer", "CAD/BIM Technician"];
const DISCIPLINES = ["Mechanical", "Electrical", "Public Health"];
const DEFAULT_STAGES = ["Stage 1", "Stage 2", "Stage 3", "Stage 4", "Stage 5", "Stage 6"];

export default function AdminSettings() {
  const [rates, setRates] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [stages, setStages] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("rates");
  const [allUsers, setAllUsers] = useState([]);
  const [branding, setBranding] = useState({});
  const [systemSettings, setSystemSettings] = useState({});
  const [gdpr, setGdpr] = useState({});

  const activeRoleNames = useMemo(() => rates.filter(r => r.active !== false).map(r => r.role), [rates]);

  useEffect(() => {
    Promise.all([
      API.get("/admin/rates"),
      API.get("/admin/deliverables"),
      API.get("/admin/stages"),
      API.get("/admin/settings"),
      API.get("/users"),
      API.get("/admin/branding"),
      API.get("/admin/system"),
      API.get("/admin/gdpr"),
    ])
      .then(([r, d, s, st, u, br, sys, gd]) => {
        setRates(r.data);
        setDeliverables(d.data);
        setStages(s.data);
        setSettings(st.data || {});
        setAllUsers(u.data || []);
        setBranding(br.data || {});
        setSystemSettings(sys.data || {});
        setGdpr(gd.data || {});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-10 w-48 bg-slate-800 rounded" /><div className="h-96 bg-slate-800 rounded-lg" /></div>;

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-100">Admin Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure rates, deliverables, and system settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="admin-tabs">
        <TabsList className="bg-slate-800 border border-slate-800 admin-tab-list flex h-auto gap-0.5 p-1">
          <TabsTrigger value="rates" data-testid="admin-tab-rates" className="text-xs whitespace-nowrap">Charge Rates</TabsTrigger>
          <TabsTrigger value="deliverables" data-testid="admin-tab-deliverables" className="text-xs whitespace-nowrap">Deliverables</TabsTrigger>
          <TabsTrigger value="templates" data-testid="admin-tab-templates" className="text-xs whitespace-nowrap">Templates</TabsTrigger>
          <TabsTrigger value="stages" data-testid="admin-tab-stages" className="text-xs whitespace-nowrap">Stages</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="text-xs whitespace-nowrap">General</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="text-xs whitespace-nowrap">Team Mgmt</TabsTrigger>
          <TabsTrigger value="theme" data-testid="admin-tab-theme" className="text-xs whitespace-nowrap">Theme</TabsTrigger>
          <TabsTrigger value="branding" data-testid="admin-tab-branding" className="text-xs whitespace-nowrap">Branding</TabsTrigger>
          <TabsTrigger value="system" data-testid="admin-tab-system" className="text-xs whitespace-nowrap">System</TabsTrigger>
          <TabsTrigger value="gdpr" data-testid="admin-tab-gdpr" className="text-xs whitespace-nowrap">Data Protection</TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-tab-email" className="text-xs whitespace-nowrap">Email</TabsTrigger>
          <TabsTrigger value="tech-systems" data-testid="admin-tab-tech-systems" className="text-xs whitespace-nowrap">Tech Systems</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit" className="text-xs whitespace-nowrap">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="rates"><RatesTab rates={rates} setRates={setRates} /></TabsContent>
        <TabsContent value="deliverables"><DeliverablesTab deliverables={deliverables} setDeliverables={setDeliverables} stageFrameworks={stages} activeRoles={activeRoleNames} /></TabsContent>
        <TabsContent value="templates"><TemplatesTab stageFrameworks={stages} /></TabsContent>
        <TabsContent value="stages"><StagesTab stages={stages} setStages={setStages} /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="settings"><GeneralTab settings={settings} setSettings={setSettings} /></TabsContent>
        <TabsContent value="users"><UsersTab users={allUsers} setUsers={setAllUsers} /></TabsContent>
        <TabsContent value="theme"><ThemeTab /></TabsContent>
        <TabsContent value="branding"><BrandingTab branding={branding} setBranding={setBranding} /></TabsContent>
        <TabsContent value="system"><SystemTab settings={systemSettings} setSettings={setSystemSettings} /></TabsContent>
        <TabsContent value="gdpr"><GDPRTab gdpr={gdpr} setGdpr={setGdpr} /></TabsContent>
        <TabsContent value="email"><EmailTab /></TabsContent>
        <TabsContent value="tech-systems"><TechnicalSystemsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function RatesTab({ rates, setRates }) {
  const confirm = useConfirm();
  const cur = useCurrency();
  const [saving, setSaving] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleCharge, setNewRoleCharge] = useState("");
  const [newRoleCost, setNewRoleCost] = useState("");
  const [addingRole, setAddingRole] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [newCon, setNewCon] = useState({ name: "", default_rate: "", default_cost_rate: "" });
  const [addingCon, setAddingCon] = useState(false);
  const [editingCon, setEditingCon] = useState(null);

  useEffect(() => { API.get("/admin/contractors").then(r => setContractors(r.data || [])).catch(() => {}); }, []);

  const updateRate = (role, field, value) => {
    setRates((prev) => prev.map((r) => r.role === role ? { ...r, [field]: parseFloat(value) || 0 } : r));
  };

  const saveRates = async () => {
    setSaving(true);
    try {
      const res = await API.put("/admin/rates", { rates });
      setRates(res.data);
      toast.success("Rates saved");
    } catch { toast.error("Failed to save rates"); }
    finally { setSaving(false); }
  };

  const addRole = async () => {
    if (!newRoleName.trim()) { toast.error("Role name is required"); return; }
    setAddingRole(true);
    try {
      const res = await API.post("/admin/rates/roles", { role: newRoleName.trim(), charge_rate: parseFloat(newRoleCharge) || 0, cost_rate: parseFloat(newRoleCost) || 0 });
      setRates(res.data);
      setNewRoleName(""); setNewRoleCharge(""); setNewRoleCost("");
      toast.success("Role added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add role"); }
    finally { setAddingRole(false); }
  };

  const archiveRole = async (role) => {
    try {
      const res = await API.put(`/admin/rates/roles/${role.id}/archive`, { archived: !role.archived });
      setRates(res.data);
      toast.success(role.archived ? "Role restored" : "Role archived");
    } catch { toast.error("Failed"); }
  };

  const deleteRole = async (role) => {
    if (!(await confirm({ title: "Delete Role", description: `Permanently delete "${role.role}"?\n\nThis cannot be undone. Historical data referencing this role may be affected.`, confirmText: "Delete Role", variant: "destructive" }))) return;
    try {
      const res = await API.delete(`/admin/rates/roles/${role.id}`);
      setRates(res.data);
      toast.success("Role deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addContractor = async () => {
    if (!newCon.name.trim()) { toast.error("Contractor name required"); return; }
    setAddingCon(true);
    try {
      const res = await API.post("/admin/contractors", { name: newCon.name.trim(), default_rate: parseFloat(newCon.default_rate) || 0, default_cost_rate: parseFloat(newCon.default_cost_rate) || 0 });
      setContractors(res.data);
      setNewCon({ name: "", default_rate: "", default_cost_rate: "" });
      toast.success("Contractor added to library");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAddingCon(false); }
  };

  const saveContractor = async (c) => {
    try {
      const res = await API.put(`/admin/contractors/${c.id}`, c);
      setContractors(res.data);
      setEditingCon(null);
      toast.success("Contractor updated");
    } catch { toast.error("Failed"); }
  };

  const deleteContractor = async (c) => {
    if (!(await confirm({ title: "Remove Contractor", description: `Remove "${c.name}" from the library?`, confirmText: "Remove", variant: "destructive" }))) return;
    try {
      const res = await API.delete(`/admin/contractors/${c.id}`);
      setContractors(res.data);
      toast.success("Contractor removed");
    } catch { toast.error("Failed"); }
  };

  const activeRates = rates.filter(r => !r.archived);
  const archivedRates = rates.filter(r => r.archived);

  return (
    <div className="space-y-6" data-testid="rates-tab">
      {/* Internal Roles */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Internal Roles — Charge & Cost Rates</h2>
        <Button onClick={saveRates} disabled={saving} data-testid="save-rates-btn" className="bg-blue-600 hover:bg-slate-700 text-white">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Rates"}
        </Button>
      </div>
      <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800/50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Charge Rate ({cur.symbol}/hr)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Cost Rate ({cur.symbol}/hr)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Margin</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeRates.map((r) => (
                <TableRow key={r.id || r.role} data-testid={`rate-row-${r.role.split(" ")[0].toLowerCase()}`}>
                  <TableCell className="px-4 py-3 text-sm font-medium">
                    {r.role}
                    {r.is_default && <span className="ml-2 text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">default</span>}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <CellInput type="number" value={r.charge_rate} onChange={(e) => updateRate(r.role, "charge_rate", e.target.value)} className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" data-testid={`charge-rate-${r.role.split(" ")[0].toLowerCase()}`} />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <CellInput type="number" value={r.cost_rate} onChange={(e) => updateRate(r.role, "cost_rate", e.target.value)} className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" data-testid={`cost-rate-${r.role.split(" ")[0].toLowerCase()}`} />
                  </TableCell>
                  <TableCell className="px-4 py-3 text-sm font-mono text-right text-emerald-600">
                    {cur.symbol}{(r.charge_rate - r.cost_rate).toFixed(0)} ({r.charge_rate > 0 ? ((r.charge_rate - r.cost_rate) / r.charge_rate * 100).toFixed(0) : 0}%)
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => archiveRole(r)} className="text-slate-600 hover:text-amber-400 p-1 rounded transition-colors" title="Archive" data-testid={`archive-role-${r.id}`}><Eye className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteRole(r)} className="text-slate-600 hover:text-rose-400 p-1 rounded transition-colors" title="Delete" data-testid={`delete-role-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add new role row */}
              <TableRow className="bg-slate-950/50">
                <TableCell className="px-4 py-2">
                  <Input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="New role name..." className="bg-slate-900 border-slate-800 h-8 text-sm" data-testid="new-role-name" />
                </TableCell>
                <TableCell className="px-4 py-2">
                  <CellInput type="number" value={newRoleCharge} onChange={e => setNewRoleCharge(e.target.value)} placeholder="0" className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" data-testid="new-role-charge" />
                </TableCell>
                <TableCell className="px-4 py-2">
                  <CellInput type="number" value={newRoleCost} onChange={e => setNewRoleCost(e.target.value)} placeholder="0" className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" data-testid="new-role-cost" />
                </TableCell>
                <TableCell colSpan={2} className="px-4 py-2">
                  <Button size="sm" onClick={addRole} disabled={addingRole} className="bg-blue-600 hover:bg-blue-500 text-white h-8" data-testid="add-role-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />{addingRole ? "Adding..." : "Add Role"}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Archived Roles */}
      {archivedRates.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800/50 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/30 py-2 px-4">
            <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Archived Roles ({archivedRates.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableBody>
                {archivedRates.map(r => (
                  <TableRow key={r.id || r.role} className="opacity-50">
                    <TableCell className="px-4 py-2 text-sm text-slate-500">{r.role}</TableCell>
                    <TableCell className="px-4 py-2 text-sm font-mono text-right text-slate-600">{cur.symbol}{r.charge_rate}</TableCell>
                    <TableCell className="px-4 py-2 text-sm font-mono text-right text-slate-600">{cur.symbol}{r.cost_rate}</TableCell>
                    <TableCell className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => archiveRole(r)} className="text-slate-600 hover:text-emerald-400 p-1 rounded transition-colors" title="Restore" data-testid={`restore-role-${r.id}`}><Eye className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteRole(r)} className="text-slate-600 hover:text-rose-400 p-1 rounded transition-colors" title="Permanently delete" data-testid={`hard-delete-role-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Contractor Library */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-800">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Contractor / Sub-Consultant Library</h2>
      </div>
      <p className="text-xs text-slate-400 -mt-4">Reusable directory of contractors. These can be added to any project from the Pricing tab.</p>
      <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800/50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Contractor Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Default Rate ({cur.symbol})</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Default Cost ({cur.symbol})</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contractors.map(c => (
                <TableRow key={c.id} data-testid={`contractor-row-${c.id}`}>
                  {editingCon === c.id ? (
                    <>
                      <TableCell className="px-4 py-2"><Input value={c.name} onChange={e => setContractors(prev => prev.map(x => x.id === c.id ? {...x, name: e.target.value} : x))} className="bg-slate-900 border-slate-800 h-8 text-sm" /></TableCell>
                      <TableCell className="px-4 py-2"><CellInput type="number" value={c.default_rate} onChange={e => setContractors(prev => prev.map(x => x.id === c.id ? {...x, default_rate: parseFloat(e.target.value) || 0} : x))} className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" /></TableCell>
                      <TableCell className="px-4 py-2"><CellInput type="number" value={c.default_cost_rate} onChange={e => setContractors(prev => prev.map(x => x.id === c.id ? {...x, default_cost_rate: parseFloat(e.target.value) || 0} : x))} className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" /></TableCell>
                      <TableCell className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => saveContractor(c)} className="text-emerald-400 hover:text-emerald-300 p-1"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setEditingCon(null)} className="text-slate-500 hover:text-slate-300 p-1"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="px-4 py-3 text-sm font-medium">{c.name}</TableCell>
                      <TableCell className="px-4 py-3 text-sm font-mono text-right">{cur.symbol}{Number(c.default_rate || 0).toLocaleString()}</TableCell>
                      <TableCell className="px-4 py-3 text-sm font-mono text-right">{cur.symbol}{Number(c.default_cost_rate || 0).toLocaleString()}</TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditingCon(c.id)} className="text-slate-600 hover:text-blue-400 p-1" data-testid={`edit-contractor-${c.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteContractor(c)} className="text-slate-600 hover:text-rose-400 p-1" data-testid={`delete-contractor-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
              {/* Add contractor row */}
              <TableRow className="bg-slate-950/50">
                <TableCell className="px-4 py-2"><Input value={newCon.name} onChange={e => setNewCon(p => ({...p, name: e.target.value}))} placeholder="Contractor name..." className="bg-slate-900 border-slate-800 h-8 text-sm" data-testid="new-contractor-name" /></TableCell>
                <TableCell className="px-4 py-2"><CellInput type="number" value={newCon.default_rate} onChange={e => setNewCon(p => ({...p, default_rate: e.target.value}))} placeholder="0" className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" data-testid="new-contractor-rate" /></TableCell>
                <TableCell className="px-4 py-2"><CellInput type="number" value={newCon.default_cost_rate} onChange={e => setNewCon(p => ({...p, default_cost_rate: e.target.value}))} placeholder="0" className="w-28 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-8" data-testid="new-contractor-cost" /></TableCell>
                <TableCell className="px-4 py-2">
                  <Button size="sm" onClick={addContractor} disabled={addingCon} className="bg-blue-600 hover:bg-blue-500 text-white h-8" data-testid="add-contractor-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />{addingCon ? "..." : "Add"}
                  </Button>
                </TableCell>
              </TableRow>
              {contractors.length === 0 && (
                <TableRow><TableCell colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">No contractors in the library yet. Add your first contractor above.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function DeliverablesTab({ deliverables, setDeliverables, stageFrameworks = [], activeRoles = [] }) {
  const [filter, setFilter] = useState({ discipline: "All", stage: "All", framework: "All" });
  const [saving, setSaving] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const csvRef = useRef(null);

  const ROLES = activeRoles.length > 0 ? activeRoles : DEFAULT_ROLES;

  // Determine if a stage belongs to a framework
  const getFrameworkForStage = (stage) => {
    if (stage.startsWith("Stage")) return "RIBA";
    if (stage.startsWith("Phase")) return "BSRIA";
    return "Other";
  };

  const filtered = deliverables.filter((d) => {
    if (filter.framework !== "All" && getFrameworkForStage(d.stage) !== filter.framework) return false;
    if (filter.discipline !== "All" && d.discipline !== filter.discipline) return false;
    if (filter.stage !== "All" && d.stage !== filter.stage) return false;
    return true;
  });

  // Build stage list from all frameworks + existing deliverable stages
  const frameworkStages = stageFrameworks.flatMap(fw => fw.stages.map(s => s.code));
  const stagesList = useMemo(
    () => [...new Set([...DEFAULT_STAGES, ...frameworkStages, ...deliverables.map((d) => d.stage)])].sort(),
    [frameworkStages, deliverables]
  );

  // Memoize custom frameworks filter for Select dropdown
  const customFrameworks = useMemo(
    () => stageFrameworks.filter(fw => fw.framework !== "RIBA" && fw.framework !== "BSRIA"),
    [stageFrameworks]
  );

  // Filter stages list by selected framework
  const filteredStagesList = filter.framework === "All"
    ? stagesList
    : stagesList.filter(s => getFrameworkForStage(s) === filter.framework);

  const addDeliverable = () => {
    const newD = {
      id: crypto.randomUUID(),
      name: "New Deliverable",
      discipline: filter.discipline !== "All" ? filter.discipline : "Mechanical",
      stage: filter.stage !== "All" ? filter.stage : "Stage 4",
      description: "",
      default_hours: {},
    };
    setDeliverables((prev) => [...prev, newD]);
  };

  const updateDeliverable = (id, field, value) => {
    setDeliverables((prev) => prev.map((d) => d.id === id ? { ...d, [field]: value } : d));
  };

  const deleteDeliverable = async (id) => {
    try {
      await API.delete(`/admin/deliverables/${id}`);
      setDeliverables((prev) => prev.filter((d) => d.id !== id));
      toast.success("Deliverable deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const saveDeliverables = async () => {
    setSaving(true);
    try {
      const res = await API.put("/admin/deliverables", { deliverables: filtered });
      toast.success("Deliverables saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) { toast.error("Please select a .csv file"); return; }
    setUploadingCsv(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await API.post("/onboarding/import-deliverables-csv?mode=append", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(r.data.message);
      if (r.data.warnings?.length) toast.warning(`${r.data.warnings.length} rows had warnings`);
      // Refresh deliverables list
      const dl = await API.get("/admin/deliverables");
      setDeliverables(dl.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV import failed");
    } finally {
      setUploadingCsv(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid="deliverables-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Select value={filter.framework || "All"} onValueChange={(v) => setFilter((prev) => ({ ...prev, framework: v, stage: "All" }))}>
            <SelectTrigger className="w-36 bg-slate-900 border-slate-800 h-8" data-testid="filter-framework"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Frameworks</SelectItem>
              <SelectItem value="RIBA">RIBA</SelectItem>
              <SelectItem value="BSRIA">BSRIA</SelectItem>
              {customFrameworks.map(fw => (
                <SelectItem key={fw.framework} value={fw.framework}>{fw.framework}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.discipline} onValueChange={(v) => setFilter((prev) => ({ ...prev, discipline: v }))}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-800 h-8" data-testid="filter-discipline"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Disciplines</SelectItem>
              {DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.stage} onValueChange={(v) => setFilter((prev) => ({ ...prev, stage: v }))}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-800 h-8" data-testid="filter-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Stages</SelectItem>
              {filteredStagesList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <input ref={csvRef} type="file" accept=".csv" onChange={handleCsvImport} className="hidden" data-testid="admin-csv-input" />
          <Button size="sm" variant="outline" onClick={() => csvRef.current?.click()} disabled={uploadingCsv} data-testid="admin-csv-import-btn" className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8">
            <Upload className="w-3 h-3 mr-1" />{uploadingCsv ? "Importing..." : "Import CSV"}
          </Button>
          <Button size="sm" onClick={addDeliverable} data-testid="add-deliverable-btn" className="bg-blue-600 hover:bg-slate-700 text-white h-8"><Plus className="w-3 h-3 mr-1" />Add</Button>
          <Button size="sm" onClick={saveDeliverables} disabled={saving} data-testid="save-deliverables-btn" className="bg-blue-600 hover:bg-blue-500/100 text-white h-8"><Save className="w-3 h-3 mr-1" />{saving ? "..." : "Save"}</Button>
        </div>
      </div>

      <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800/50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-3">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-3">Discipline</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-3">Stage</TableHead>
                {ROLES.map((r) => <TableHead key={r} className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-2 text-center w-16"><span>{r.split(" ")[0].substring(0, 3)}</span><span className="block text-[9px] font-normal normal-case text-slate-600">(hrs)</span></TableHead>)}
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id} data-testid={`deliverable-row-${d.id}`}>
                  <TableCell className="px-3">
                    <Input value={d.name} onChange={(e) => updateDeliverable(d.id, "name", e.target.value)} className="h-7 text-xs border-slate-800 bg-slate-900" />
                  </TableCell>
                  <TableCell className="px-3">
                    <Select value={d.discipline} onValueChange={(v) => updateDeliverable(d.id, "discipline", v)}>
                      <SelectTrigger className="h-7 text-xs border-slate-800 bg-slate-900 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>{DISCIPLINES.map((disc) => <SelectItem key={disc} value={disc}>{disc}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="px-3">
                    <Select value={d.stage} onValueChange={(v) => updateDeliverable(d.id, "stage", v)}>
                      <SelectTrigger className="h-7 text-xs border-slate-800 bg-slate-900 w-28" data-testid={`stage-select-${d.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stagesList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  {ROLES.map((role) => (
                    <TableCell key={role} className="px-1">
                      <Input
                        type="number"
                        value={d.default_hours?.[role] || ""}
                        onChange={(e) => updateDeliverable(d.id, "default_hours", { ...d.default_hours, [role]: parseFloat(e.target.value) || 0 })}
                        className="w-14 h-7 text-center font-mono text-xs border-slate-800 bg-slate-900"
                      />
                    </TableCell>
                  ))}
                  <TableCell className="px-2">
                    <button onClick={() => deleteDeliverable(d.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <p className="text-xs text-slate-400">{filtered.length} deliverables shown</p>
    </div>
  );
}

function StagesTab({ stages, setStages }) {
  const confirm = useConfirm();
  const [editingFw, setEditingFw] = useState(null);
  const [editStages, setEditStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newFwName, setNewFwName] = useState("");
  const [showNewFw, setShowNewFw] = useState(false);

  const startEdit = (fw) => {
    setEditingFw(fw.framework);
    setEditStages([...fw.stages]);
  };

  const cancelEdit = () => {
    setEditingFw(null);
    setEditStages([]);
  };

  const addStage = () => {
    const num = editStages.length + 1;
    setEditStages([...editStages, { number: num, code: `Stage ${num}`, name: "" }]);
  };

  const removeStage = (idx) => {
    setEditStages(editStages.filter((_, i) => i !== idx));
  };

  const updateStage = (idx, field, value) => {
    setEditStages(editStages.map((s, i) => i === idx ? { ...s, [field]: field === "number" ? parseInt(value) || 0 : value } : s));
  };

  const saveStages = async (framework) => {
    setSaving(true);
    try {
      const res = await API.put("/admin/stages", { framework, stages: editStages });
      setStages(prev => prev.map(fw => fw.framework === framework ? res.data : fw));
      setEditingFw(null);
      toast.success(`${framework} stages saved`);
    } catch { toast.error("Failed to save stages"); }
    finally { setSaving(false); }
  };

  const createFramework = async () => {
    if (!newFwName.trim()) { toast.error("Name is required"); return; }
    try {
      const res = await API.post("/admin/stages", { framework: newFwName.trim(), stages: [{ number: 1, code: "Stage 1", name: "Preparation" }] });
      setStages(prev => [...prev, res.data]);
      setNewFwName("");
      setShowNewFw(false);
      toast.success("Framework created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
  };

  const deleteFramework = async (framework) => {
    if (!(await confirm({ title: "Delete Framework", description: `Delete "${framework}" framework?`, confirmText: "Delete", variant: "destructive" }))) return;
    try {
      await API.delete(`/admin/stages/${encodeURIComponent(framework)}`);
      setStages(prev => prev.filter(fw => fw.framework !== framework));
      toast.success("Framework deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="stages-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Stage Frameworks</h2>
        <Button size="sm" onClick={() => setShowNewFw(!showNewFw)} className="bg-blue-600 hover:bg-blue-500 text-white h-8" data-testid="add-framework-btn">
          <Plus className="w-3 h-3 mr-1" />{showNewFw ? "Cancel" : "Add Framework"}
        </Button>
      </div>

      {showNewFw && (
        <Card className="bg-slate-900 border border-blue-500/20 shadow-sm">
          <CardContent className="p-4 flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Framework Name</Label>
              <Input value={newFwName} onChange={(e) => setNewFwName(e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800" placeholder="e.g. BSRIA Custom" data-testid="new-framework-name" />
            </div>
            <Button size="sm" onClick={createFramework} className="bg-blue-600 hover:bg-blue-500 text-white h-9" data-testid="create-framework-btn"><Check className="w-3 h-3 mr-1" />Create</Button>
          </CardContent>
        </Card>
      )}

      {stages.map((fw) => (
        <Card key={fw.framework} className="bg-slate-900 border border-slate-800 shadow-sm" data-testid={`framework-${fw.framework.toLowerCase().replace(/\s/g, '-')}`}>
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">{fw.framework}</CardTitle>
              <div className="flex items-center gap-2">
                {editingFw === fw.framework ? (
                  <>
                    <Button size="sm" onClick={() => saveStages(fw.framework)} disabled={saving} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`save-stages-${fw.framework.toLowerCase()}`}>
                      <Save className="w-3 h-3 mr-1" />{saving ? "..." : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 text-xs border-slate-700" data-testid={`cancel-stages-${fw.framework.toLowerCase()}`}>
                      <X className="w-3 h-3 mr-1" />Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => startEdit(fw)} className="h-7 text-xs border-slate-700" data-testid={`edit-stages-${fw.framework.toLowerCase()}`}>
                      <Edit2 className="w-3 h-3 mr-1" />Edit
                    </Button>
                    <button onClick={() => deleteFramework(fw.framework)} className="text-slate-500 hover:text-rose-400 transition-colors p-1" data-testid={`delete-framework-${fw.framework.toLowerCase()}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800/50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 w-20">Number</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Code</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Name</TableHead>
                  {editingFw === fw.framework && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(editingFw === fw.framework ? editStages : fw.stages).map((s, idx) => (
                  <TableRow key={s.code || `stage-${idx}`}>
                    <TableCell className="px-4 py-2">
                      {editingFw === fw.framework ? (
                        <CellInput type="number" value={s.number} onChange={(e) => updateStage(idx, "number", e.target.value)} className="h-7 w-16 font-mono text-sm bg-slate-950 border-slate-800" />
                      ) : (
                        <span className="font-mono text-sm">{s.number}</span>
                      )}
                    </TableCell>
                    <TableCell className="px-4 py-2">
                      {editingFw === fw.framework ? (
                        <Input value={s.code} onChange={(e) => updateStage(idx, "code", e.target.value)} className="h-7 text-sm bg-slate-950 border-slate-800" />
                      ) : (
                        <span className="text-sm font-medium">{s.code}</span>
                      )}
                    </TableCell>
                    <TableCell className="px-4 py-2">
                      {editingFw === fw.framework ? (
                        <Input value={s.name} onChange={(e) => updateStage(idx, "name", e.target.value)} className="h-7 text-sm bg-slate-950 border-slate-800" />
                      ) : (
                        <span className="text-sm text-slate-400">{s.name}</span>
                      )}
                    </TableCell>
                    {editingFw === fw.framework && (
                      <TableCell className="px-2">
                        <button onClick={() => removeStage(idx)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {editingFw === fw.framework && (
              <div className="p-3 border-t border-slate-800">
                <Button size="sm" variant="outline" onClick={addStage} className="h-7 text-xs border-slate-700" data-testid={`add-stage-${fw.framework.toLowerCase()}`}>
                  <Plus className="w-3 h-3 mr-1" />Add Stage
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function GeneralTab({ settings, setSettings }) {
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { setCurrency } = useCurrency();

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/settings", settings);
      if (settings.currency_code) setCurrency(settings.currency_code);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await API.get("/admin/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mep-engine-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const updateProcurementEffect = (route, value) => {
    const effects = { ...(settings.procurement_route_effects || {}) };
    effects[route] = parseFloat(value) || 1.0;
    setSettings(prev => ({ ...prev, procurement_route_effects: effects }));
  };

  const updateProgrammeMult = (type, value) => {
    const mults = { ...(settings.programme_duration_multipliers || {}) };
    mults[type] = parseFloat(value) || 1.0;
    setSettings(prev => ({ ...prev, programme_duration_multipliers: mults }));
  };

  const updateEffortDriver = (idx, field, value) => {
    const drivers = [...(settings.effort_drivers || [])];
    drivers[idx] = { ...drivers[idx], [field]: field === "multiplier" ? (parseFloat(value) || 1.0) : field === "active" ? value : value };
    setSettings(prev => ({ ...prev, effort_drivers: drivers }));
  };

  const addEffortDriver = () => {
    const drivers = [...(settings.effort_drivers || []), { name: "New Driver", multiplier: 1.05, active: true }];
    setSettings(prev => ({ ...prev, effort_drivers: drivers }));
  };

  const removeEffortDriver = (idx) => {
    const drivers = [...(settings.effort_drivers || [])];
    drivers.splice(idx, 1);
    setSettings(prev => ({ ...prev, effort_drivers: drivers }));
  };

  return (
    <div className="space-y-6" data-testid="general-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-slate-100">General Settings</h2>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-blue-600 hover:bg-slate-700 text-white">
            <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Settings"}
          </Button>
          <Button variant="outline" onClick={exportData} disabled={exporting} data-testid="export-data-btn" className="border-slate-800">
            {exporting ? "Exporting..." : "Export All Data"}
          </Button>
        </div>
      </div>

      {/* Core Settings */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Core Settings</CardTitle></CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-4 gap-5">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Benchmark Target (%)</Label>
            <CellInput type="number" step="0.5" value={settings.benchmark_target_percentage || ""} onChange={(e) => setSettings(prev => ({ ...prev, benchmark_target_percentage: parseFloat(e.target.value) || 0 }))} className="mt-1.5 font-mono bg-slate-900 border-slate-800" data-testid="benchmark-target" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Default Complexity Factor</Label>
            <CellInput type="number" step="0.05" value={settings.default_complexity_factor || ""} onChange={(e) => setSettings(prev => ({ ...prev, default_complexity_factor: parseFloat(e.target.value) || 0 }))} className="mt-1.5 font-mono bg-slate-900 border-slate-800" data-testid="default-complexity" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Discount Threshold (%)</Label>
            <CellInput type="number" step="1" value={settings.discount_threshold || ""} onChange={(e) => setSettings(prev => ({ ...prev, discount_threshold: parseFloat(e.target.value) || 0 }))} className="mt-1.5 font-mono bg-slate-900 border-slate-800" data-testid="discount-threshold" />
            <p className="text-xs text-slate-400 mt-1">Projects exceeding this discount will be auto-flagged for approval</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours per Day</Label>
            <CellInput type="number" step="0.5" min="1" max="24" value={settings.hours_per_day ?? 8} onChange={(e) => setSettings(prev => ({ ...prev, hours_per_day: parseFloat(e.target.value) || 8 }))} className="mt-1.5 font-mono bg-slate-900 border-slate-800" data-testid="hours-per-day" />
            <p className="text-xs text-slate-400 mt-1">Used when deliverables are priced in days instead of hours</p>
          </div>
        </CardContent>
      </Card>

      {/* Currency Settings */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Currency Settings</CardTitle></CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Currency</Label>
              <Select value={settings.currency_code || "GBP"} onValueChange={(v) => setSettings(prev => ({ ...prev, currency_code: v }))}>
                <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800" data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="font-mono mr-2">{c.symbol}</span> {c.name} ({c.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">All monetary values across the platform will display in this currency</p>
            </div>
            <div className="flex items-end">
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg px-5 py-3.5">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Preview</p>
                <p className="text-lg font-mono font-bold text-slate-100 mt-1" data-testid="currency-preview">
                  {(CURRENCIES.find(c => c.code === (settings.currency_code || "GBP")) || CURRENCIES[0]).symbol}125,000.00
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Rate: {(CURRENCIES.find(c => c.code === (settings.currency_code || "GBP")) || CURRENCIES[0]).symbol}150/hr
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Procurement Route Effects */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Procurement Route Effects</CardTitle></CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(settings.procurement_route_effects || {}).map(([route, mult]) => (
              <div key={route}>
                <Label className="text-xs font-semibold text-slate-500">{route}</Label>
                <CellInput type="number" step="0.05" value={mult} onChange={(e) => updateProcurementEffect(route, e.target.value)} className="mt-1 font-mono bg-slate-900 border-slate-800" data-testid={`procurement-effect-${route.replace(/\s/g, '-').toLowerCase()}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Programme Duration Multipliers */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Programme Duration Multipliers</CardTitle></CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(settings.programme_duration_multipliers || {}).map(([type, mult]) => (
              <div key={type}>
                <Label className="text-xs font-semibold text-slate-500">{type}</Label>
                <CellInput type="number" step="0.05" value={mult} onChange={(e) => updateProgrammeMult(type, e.target.value)} className="mt-1 font-mono bg-slate-900 border-slate-800" data-testid={`programme-mult-${type.toLowerCase()}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Effort Drivers */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Effort Drivers</CardTitle>
            <Button size="sm" onClick={addEffortDriver} className="bg-blue-600 hover:bg-slate-700 text-white h-7 text-xs" data-testid="add-effort-driver-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {(settings.effort_drivers || []).map((driver, i) => (
            <div key={driver.id || `driver-${i}`} className="flex items-center gap-3 border border-slate-800 rounded-md p-3" data-testid={`effort-driver-${i}`}>
              <Input value={driver.name} onChange={(e) => updateEffortDriver(i, "name", e.target.value)} className="flex-1 h-8 text-sm bg-slate-900 border-slate-800" />
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-slate-500 whitespace-nowrap">Multiplier</Label>
                <CellInput type="number" step="0.01" value={driver.multiplier} onChange={(e) => updateEffortDriver(i, "multiplier", e.target.value)} className="w-24 h-8 font-mono text-xs bg-slate-900 border-slate-800 text-center" />
              </div>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={driver.active} onChange={(e) => updateEffortDriver(i, "active", e.target.checked)} className="rounded border-slate-800" />
                Active
              </label>
              <button onClick={() => removeEffortDriver(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {(!settings.effort_drivers || settings.effort_drivers.length === 0) && <p className="text-sm text-slate-400 text-center py-3">No effort drivers configured.</p>}
        </CardContent>
      </Card>

      {/* Reference Data */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Reference Data</CardTitle></CardHeader>
        <CardContent className="p-4 space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Risk Categories</Label>
            <p className="text-sm text-slate-400 mt-1">{(settings.risk_categories || []).join(", ")}</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sectors</Label>
            <p className="text-sm text-slate-400 mt-1">{(settings.sectors || []).join(", ")}</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Project Types</Label>
            <p className="text-sm text-slate-400 mt-1">{(settings.project_types || []).join(", ")}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function TemplatesTab({ stageFrameworks = [] }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const ALL_DISCIPLINES = ["Mechanical", "Electrical", "Public Health"];
  const SECTORS = ["Commercial", "Residential", "Healthcare", "Education", "Industrial", "Retail", "Mixed Use", "Data Centre", "Leisure"];
  const PROJECT_TYPES = ["New Build", "Refurbishment", "Fit Out", "Extension", "Conversion", "Renovation"];

  const getStagesForFramework = (frameworkName) => {
    const fw = stageFrameworks.find(f => f.framework === frameworkName);
    return fw ? fw.stages.map(s => s.code) : [];
  };

  const availableFrameworks = stageFrameworks.map(f => f.framework);

  useEffect(() => {
    API.get("/admin/templates")
      .then((r) => setTemplates(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const addTemplate = async () => {
    const defaultFw = "RIBA";
    const defaultStages = getStagesForFramework(defaultFw).filter(s => s !== "Stage 0" && s !== "Stage 1" && s !== "Stage 7");
    const newT = { name: "New Template", sector: "", project_type: "New Build", disciplines: ALL_DISCIPLINES, stage_framework: defaultFw, description: "", default_stages: defaultStages.length ? defaultStages : ["Stage 2", "Stage 3", "Stage 4", "Stage 5", "Stage 6"] };
    try {
      const res = await API.post("/admin/templates", newT);
      setTemplates([...templates, res.data]);
      toast.success("Template created");
    } catch { toast.error("Failed to create"); }
  };

  const updateTemplate = async (id, data) => {
    try {
      await API.put(`/admin/templates/${id}`, data);
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...data } : t));
    } catch { toast.error("Failed to save"); }
  };

  const handleFrameworkChange = (tpl, newFramework) => {
    const newStages = getStagesForFramework(newFramework);
    updateTemplate(tpl.id, { stage_framework: newFramework, default_stages: newStages });
  };

  const deactivate = async (id) => {
    try {
      await API.delete(`/admin/templates/${id}`);
      setTemplates(templates.filter((t) => t.id !== id));
      toast.success("Deactivated");
    } catch { toast.error("Failed"); }
  };

  const toggleStage = (tpl, stage) => {
    const current = tpl.default_stages || [];
    const next = current.includes(stage) ? current.filter(s => s !== stage) : [...current, stage].sort();
    updateTemplate(tpl.id, { default_stages: next });
  };

  const toggleDiscipline = (tpl, disc) => {
    const current = tpl.disciplines || [];
    const next = current.includes(disc) ? current.filter(d => d !== disc) : [...current, disc];
    updateTemplate(tpl.id, { disciplines: next });
  };

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-100">Project Templates</h2>
          <p className="text-xs text-slate-500 mt-0.5">When a template is used to create a project, it pre-fills project details AND automatically loads scope deliverables for the selected disciplines and stages.</p>
        </div>
        <Button size="sm" onClick={addTemplate} className="bg-blue-600 hover:bg-slate-700 text-white h-8" data-testid="add-template-btn"><Plus className="w-3 h-3 mr-1" />Add Template</Button>
      </div>
      {loading ? <div className="h-32 bg-slate-800 rounded animate-pulse" /> : (
        <div className="space-y-4">
          {templates.map((t) => {
            const templateStages = getStagesForFramework(t.stage_framework || "RIBA");
            return (
            <Card key={t.id} className="bg-slate-900 border border-slate-800 shadow-sm" data-testid={`template-card-${t.id}`}>
              <CardContent className="p-5 space-y-4">
                {/* Row 1: Name, Sector, Type, Description */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</Label>
                    <Input defaultValue={t.name} onBlur={(e) => updateTemplate(t.id, { name: e.target.value })} className="mt-1 h-8 text-sm bg-slate-900 border-slate-800" data-testid={`template-name-${t.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sector</Label>
                    <Select value={t.sector || ""} onValueChange={(v) => updateTemplate(t.id, { sector: v })}>
                      <SelectTrigger className="mt-1 h-8 text-sm bg-slate-900 border-slate-800" data-testid={`template-sector-${t.id}`}><SelectValue placeholder="Select sector" /></SelectTrigger>
                      <SelectContent>
                        {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Project Type</Label>
                    <Select value={t.project_type || ""} onValueChange={(v) => updateTemplate(t.id, { project_type: v })}>
                      <SelectTrigger className="mt-1 h-8 text-sm bg-slate-900 border-slate-800" data-testid={`template-type-${t.id}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {PROJECT_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</Label>
                      <Input defaultValue={t.description} onBlur={(e) => updateTemplate(t.id, { description: e.target.value })} className="mt-1 h-8 text-sm bg-slate-900 border-slate-800" data-testid={`template-desc-${t.id}`} />
                    </div>
                    <button onClick={() => deactivate(t.id)} className="text-slate-400 hover:text-red-500 mb-0.5" data-testid={`template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {/* Row 2: Framework + Disciplines */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Stage Framework</Label>
                    <Select value={t.stage_framework || "RIBA"} onValueChange={(v) => handleFrameworkChange(t, v)}>
                      <SelectTrigger className="h-8 text-sm bg-slate-900 border-slate-800 w-48" data-testid={`template-framework-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availableFrameworks.map(fw => <SelectItem key={fw} value={fw}>{fw}</SelectItem>)}
                        {availableFrameworks.length === 0 && <SelectItem value="RIBA">RIBA</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Disciplines</Label>
                    <div className="flex gap-2">
                      {ALL_DISCIPLINES.map(d => {
                        const active = (t.disciplines || []).includes(d);
                        return (
                          <button key={d} onClick={() => toggleDiscipline(t, d)} data-testid={`template-disc-${t.id}-${d.charAt(0).toLowerCase()}`}
                            className={`px-3 py-1 rounded text-xs font-medium border transition-all ${active ? "bg-blue-500/10 border-blue-500/30 text-blue-400" : "bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-600"}`}
                          >{d}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {/* Row 3: Included Stages - dynamic based on framework */}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                    Included Stages — {t.stage_framework || "RIBA"} <span className="normal-case font-normal">(scope deliverables will be loaded for these)</span>
                  </Label>
                  <div className="flex gap-2 flex-wrap">
                    {templateStages.length > 0 ? templateStages.map(s => {
                      const active = (t.default_stages || []).includes(s);
                      return (
                        <button key={s} onClick={() => toggleStage(t, s)} data-testid={`template-stage-${t.id}-${s.replace(/ /g, "")}`}
                          className={`px-3 py-1 rounded text-xs font-medium border transition-all ${active ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-600"}`}
                        >{s}</button>
                      );
                    }) : (
                      <p className="text-xs text-slate-600 italic">No stages found for framework "{t.stage_framework}". Check Stage Frameworks tab.</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
          {templates.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No templates. Click "Add Template" to create one.</p>}
        </div>
      )}
    </div>
  );
}

// ─── Theme Tab ───
function ThemeTab() {
  const { theme, setTheme, source } = useTheme();
  const { isDirector } = useAuth();
  const [saving, setSaving] = useState(false);
  const [platformTheme, setPlatformTheme] = useState("dark");

  useEffect(() => {
    API.get("/admin/theme").then(r => setPlatformTheme(r.data.theme || "dark")).catch(() => {});
  }, []);

  const themes = [
    { id: "dark", label: "Dark", desc: "Professional dark theme with slate and blue accents", icon: Moon, preview: "bg-slate-950 border-slate-800" },
    { id: "light", label: "Light", desc: "Clean light theme for well-lit environments", icon: Sun, preview: "bg-white border-slate-200" },
    { id: "colourful", label: "Colourful", desc: "Vibrant purple-accent theme for a distinctive look", icon: Palette, preview: "bg-[hsl(260,30%,6%)] border-purple-800" },
  ];

  const applyUserTheme = async (t) => {
    setSaving(true);
    await setTheme(t);
    setSaving(false);
    toast.success(`Your theme set to ${t}`);
  };

  const setPlatformDefault = async (t) => {
    try {
      await API.put("/admin/theme", { theme: t });
      setPlatformTheme(t);
      toast.success(`Platform default set to ${t}`);
    } catch { toast.error("Failed to set platform default"); }
  };

  return (
    <div className="space-y-8" data-testid="theme-tab">
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-100">Your Theme</h2>
        <p className="text-sm text-slate-500 mt-1">
          Choose your personal theme. This overrides the platform default.
          {source === "platform" && <span className="text-blue-400 ml-1">(Currently using platform default)</span>}
          {source === "user" && <span className="text-emerald-400 ml-1">(Personal preference active)</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {themes.map((t) => {
          const isActive = theme === t.id;
          return (
            <button
              key={t.id}
              onClick={() => applyUserTheme(t.id)}
              disabled={saving}
              data-testid={`theme-option-${t.id}`}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                isActive
                  ? "border-blue-500 ring-2 ring-blue-500/20 bg-slate-800/50"
                  : "border-slate-800 hover:border-slate-700 bg-slate-900"
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg ${isActive ? "bg-blue-500/20" : "bg-slate-800"} flex items-center justify-center`}>
                  <t.icon className={`w-5 h-5 ${isActive ? "text-blue-400" : "text-slate-500"}`} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-100">{t.label}</p>
                  {isActive && <span className="text-[10px] font-bold uppercase text-blue-400">Active</span>}
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{t.desc}</p>
              <div className={`mt-3 h-8 rounded-md border ${t.preview}`} />
            </button>
          );
        })}
      </div>

      {isDirector && (
        <div className="pt-6 border-t border-slate-800">
          <h2 className="font-heading text-lg font-semibold text-slate-100">Platform Default Theme</h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">Set the default theme for new users and users who haven't chosen a personal preference.</p>
          <div className="flex items-center gap-3">
            {themes.map((t) => (
              <button
                key={t.id}
                onClick={() => setPlatformDefault(t.id)}
                data-testid={`platform-theme-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm font-medium ${
                  platformTheme === t.id
                    ? "border-blue-500 bg-blue-500/10 text-blue-400"
                    : "border-slate-800 text-slate-400 hover:border-slate-700 hover:bg-slate-800/50"
                }`}
              >
                <t.icon className="w-4 h-4" strokeWidth={1.5} />
                {t.label}
                {platformTheme === t.id && <span className="text-[10px] font-bold uppercase text-blue-400 ml-1">Default</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AuditTab() {
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/admin/audit?limit=200")
      .then((r) => setAudit(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <h2 className="font-heading text-lg font-semibold text-slate-100">System Audit Trail</h2>
      <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800/50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Timestamp</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Action</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Project ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <TableRow key={`skeleton-${i}`}><TableCell colSpan={4}><div className="h-4 bg-slate-800 rounded animate-pulse" /></TableCell></TableRow>)
              ) : audit.length > 0 ? (
                audit.map((a, i) => (
                  <TableRow key={a.id || `audit-${a.timestamp}-${i}`} data-testid={`admin-audit-${i}`}>
                    <TableCell className="px-4 py-2 font-mono text-xs text-slate-500">{new Date(a.timestamp).toLocaleString("en-GB")}</TableCell>
                    <TableCell className="px-4 py-2"><span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400 font-medium">{a.action}</span></TableCell>
                    <TableCell className="px-4 py-2 text-sm text-slate-300">{a.description}</TableCell>
                    <TableCell className="px-4 py-2 font-mono text-xs text-slate-400">{a.project_id?.substring(0, 8)}...</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-slate-400">No audit records</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


// ─── Users/Team Management Tab ───
function UsersTab({ users, setUsers }) {
  const confirm = useConfirm();
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editTier, setEditTier] = useState(1);
  const [editCanCreate, setEditCanCreate] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "Engineer", password: "" });
  const [inviting, setInviting] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");
  const [bulkSelection, setBulkSelection] = useState(new Set());
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Memoize filtered project list for assignment dropdown
  const filteredProjects = useMemo(() => {
    if (!assigningUser) return [];
    const assignedIds = (assigningUser.assigned_projects || []);
    return projects
      .filter(p => !assignedIds.includes(p.id))
      .filter(p => !projectSearch || p.name?.toLowerCase().includes(projectSearch.toLowerCase()) || p.client_name?.toLowerCase().includes(projectSearch.toLowerCase()));
  }, [projects, assigningUser, projectSearch]);

  const ROLE_COLORS = {
    Admin: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    Director: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    "Senior Engineer": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    Engineer: "bg-slate-800 text-slate-400 border-slate-800",
  };

  const TIER_COLORS = {
    1: "bg-slate-800 text-slate-400 border-slate-700",
    2: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    3: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    4: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    5: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  };

  const TIER_NAMES = {
    1: "Timesheet Only",
    2: "Contributor",
    3: "Project Lead",
    4: "Senior Reviewer",
    5: "Director",
  };

  const TIER_DESCRIPTIONS = {
    1: "Timesheets tab only. Can log hours and submit weeks.",
    2: "Timesheets + Scope (view & edit) + Files (view & upload).",
    3: "Tier 2 + Overview + Risk + Pricing (no margin) + Approval (submit).",
    4: "Tier 3 + Internal pricing + Proposals + Approval (approve/reject).",
    5: "Full access. Admin settings, benchmarking, intelligence.",
  };

  useEffect(() => {
    API.get("/projects").then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const startEditing = (u) => {
    setEditingId(u.id);
    setEditRole(u.role);
    setEditTier(u.permission_tier || (u.role === "Director" || u.role === "Admin" ? 5 : 1));
    setEditCanCreate(u.can_create_projects || false);
  };

  const savePermissions = async (userId) => {
    setSaving(true);
    try {
      const r = await API.put(`/admin/users/${userId}`, {
        role: editRole,
        permission_tier: editTier,
        can_create_projects: editCanCreate,
      });
      setUsers(prev => prev.map(u => u.id === userId ? r.data : u));
      setEditingId(null);
      toast.success("Permissions updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const resetPassword = async (userId) => {
    try {
      await API.put(`/admin/users/${userId}/reset-password`, { password: resetPw || "reset123" });
      toast.success(`Password reset to "${resetPw || "reset123"}"`);
      setResetPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const inviteUser = async () => {
    if (!inviteForm.email || !inviteForm.name) { toast.error("Email and name are required"); return; }
    setInviting(true);
    try {
      const r = await API.post("/admin/invite", inviteForm);
      toast.success(`${r.data.email} invited. Temporary password: ${r.data.temp_password}`);
      setUsers(prev => [...prev, r.data.user]);
      setInviteForm({ email: "", name: "", role: "Engineer", password: "" });
      setShowInvite(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
    finally { setInviting(false); }
  };

  const removeUser = async (userId, userName) => {
    if (!(await confirm({ title: "Remove User", description: `Remove ${userName} from the platform? This cannot be undone.`, confirmText: "Remove User", variant: "destructive" }))) return;
    try {
      await API.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success(`${userName} removed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove user"); }
  };

  const assignProject = async (userId, projectId) => {
    try {
      const r = await API.post(`/admin/users/${userId}/assign-project`, { project_id: projectId });
      setUsers(prev => prev.map(u => u.id === userId ? r.data : u));
      toast.success("Project assigned");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulkAssignProjects = async (userId) => {
    if (bulkSelection.size === 0) return;
    setBulkAssigning(true);
    let lastResult = null;
    let count = 0;
    for (const pid of bulkSelection) {
      try {
        lastResult = await API.post(`/admin/users/${userId}/assign-project`, { project_id: pid });
        count++;
      } catch {}
    }
    if (lastResult) {
      setUsers(prev => prev.map(u => u.id === userId ? lastResult.data : u));
    }
    toast.success(`${count} project${count > 1 ? "s" : ""} assigned`);
    setBulkSelection(new Set());
    setBulkAssigning(false);
  };

  const toggleBulkSelect = (projectId) => {
    setBulkSelection(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const selectAllUnassigned = () => {
    setBulkSelection(new Set(filteredProjects.map(p => p.id)));
  };

  const deselectAll = () => setBulkSelection(new Set());

  const unassignProject = async (userId, projectId) => {
    try {
      const r = await API.delete(`/admin/users/${userId}/unassign-project/${projectId}`);
      setUsers(prev => prev.map(u => u.id === userId ? r.data : u));
      toast.success("Project unassigned");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const searchFilteredProjects = projects.filter(p => p.name?.toLowerCase().includes(projectSearch.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      {/* Tier Legend */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Permission Tiers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            {[1,2,3,4,5].map(t => (
              <div key={t} className="flex flex-col gap-1 p-2 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${TIER_COLORS[t]}`}>{t}</span>
                  <span className="text-xs font-semibold text-foreground">{TIER_NAMES[t]}</span>
                </div>
                <p className="text-[10px] text-muted-foreground leading-relaxed">{TIER_DESCRIPTIONS[t]}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invite Panel */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground">Team Management & Permissions</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Manage accounts, permission tiers, and project assignments. Users can only see projects they are assigned to.</p>
        </div>
        <Button onClick={() => setShowInvite(!showInvite)} data-testid="invite-user-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
          <UserPlus className="w-4 h-4 mr-1.5" />{showInvite ? "Cancel" : "Invite User"}
        </Button>
      </div>

      {showInvite && (
        <Card className="bg-slate-900 border-2 border-blue-500/20" data-testid="invite-form">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-100">Invite New User</h3>
            <p className="text-xs text-slate-500">New users start at Tier 1 (Timesheet Only). You can upgrade their permissions after they join.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Full Name *</Label>
                <Input value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jane Smith" className="mt-1.5 bg-slate-900 border-slate-800" data-testid="invite-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Email *</Label>
                <Input value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="e.g. jane@company.com" className="mt-1.5 bg-slate-900 border-slate-800" data-testid="invite-email" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Role</Label>
                <Select value={inviteForm.role} onValueChange={v => setInviteForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Director", "Senior Engineer", "Engineer"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Temporary Password</Label>
                <Input value={inviteForm.password} onChange={e => setInviteForm(f => ({ ...f, password: e.target.value }))} placeholder="Leave blank for auto-generated" className="mt-1.5 bg-slate-900 border-slate-800" data-testid="invite-password" />
              </div>
            </div>
            <Button onClick={inviteUser} disabled={inviting} data-testid="send-invite-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* User Cards with Permissions */}
      <div className="space-y-3">
        {users.map(u => {
          const isEditing = editingId === u.id;
          const isAssigning = assigningUser === u.id;
          const uTier = u.permission_tier || (u.role === "Director" || u.role === "Admin" ? 5 : 1);
          const isUserDirector = u.role === "Director" || u.role === "Admin";
          const assignedIds = u.assigned_projects || [];
          const assignedProjects = projects.filter(p => assignedIds.includes(p.id));

          return (
            <Card key={u.id} className="bg-slate-900 border-slate-800" data-testid={`user-card-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {/* User info */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-blue-400">{u.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-100 truncate">{u.name}</p>
                      <p className="text-xs text-slate-500 font-mono truncate">{u.email}</p>
                    </div>
                  </div>

                  {/* Tier & Role badges */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${ROLE_COLORS[u.role] || ROLE_COLORS.Engineer}`}>{u.role}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${TIER_COLORS[uTier]}`}>Tier {uTier}: {TIER_NAMES[uTier]}</span>
                    {u.can_create_projects && !isUserDirector && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Can Create</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!isEditing && !isUserDirector && (
                      <Button size="sm" variant="outline" onClick={() => startEditing(u)} className="h-7 text-xs border-slate-800" data-testid={`edit-perms-${u.id}`}>
                        <Shield className="w-3 h-3 mr-1" />Permissions
                      </Button>
                    )}
                    {!isUserDirector && (
                      <Button size="sm" variant="outline" onClick={() => { setAssigningUser(isAssigning ? null : u.id); setBulkSelection(new Set()); setProjectSearch(""); }} className="h-7 text-xs border-slate-800" data-testid={`assign-btn-${u.id}`}>
                        Projects ({assignedIds.length})
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => resetPassword(u.id)} className="h-7 text-xs border-slate-800" data-testid={`reset-pw-${u.id}`}>Reset PW</Button>
                    <Button size="sm" variant="outline" onClick={() => removeUser(u.id, u.name)} className="h-7 text-xs border-rose-500/20 text-rose-400 hover:bg-rose-500/10" data-testid={`remove-user-${u.id}`}>Remove</Button>
                  </div>
                </div>

                {/* Editing permissions inline */}
                {isEditing && (
                  <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3" data-testid={`edit-perms-panel-${u.id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Role</Label>
                        <Select value={editRole} onValueChange={setEditRole}>
                          <SelectTrigger className="mt-1 h-8 text-xs bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["Director", "Senior Engineer", "Engineer"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Permission Tier</Label>
                        <Select value={String(editTier)} onValueChange={v => setEditTier(Number(v))}>
                          <SelectTrigger className="mt-1 h-8 text-xs bg-slate-900 border-slate-700" data-testid={`tier-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5].map(t => <SelectItem key={t} value={String(t)}>Tier {t}: {TIER_NAMES[t]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 h-8 cursor-pointer">
                          <Switch checked={editCanCreate} onCheckedChange={setEditCanCreate} data-testid={`can-create-toggle-${u.id}`} />
                          <span className="text-xs text-slate-400">Can Create Projects</span>
                        </label>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500">{TIER_DESCRIPTIONS[editTier]}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => savePermissions(u.id)} disabled={saving} className="h-7 text-xs bg-blue-600 hover:bg-blue-500 text-white" data-testid={`save-perms-${u.id}`}>
                        {saving ? "Saving..." : "Save Permissions"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-7 text-xs border-slate-700">Cancel</Button>
                    </div>
                  </div>
                )}

                {/* Project assignment panel */}
                {isAssigning && (
                  <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3" data-testid={`assign-panel-${u.id}`}>
                    <h4 className="text-xs font-semibold text-slate-300">Assigned Projects ({assignedIds.length})</h4>
                    {assignedProjects.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {assignedProjects.map(p => (
                          <span key={p.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-400 text-xs border border-blue-500/20">
                            {p.name}
                            <button onClick={() => unassignProject(u.id, p.id)} className="text-blue-400/60 hover:text-rose-400 transition-colors" data-testid={`unassign-${u.id}-${p.id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">No projects assigned. This user cannot see any projects.</p>
                    )}

                    {/* Bulk multi-select assignment */}
                    <div className="border-t border-slate-700 pt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Add Projects</Label>
                        {projects.filter(p => !assignedIds.includes(p.id)).length > 0 && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => selectAllUnassigned()} className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors" data-testid={`select-all-${u.id}`}>Select All</button>
                            <span className="text-slate-700">|</span>
                            <button onClick={deselectAll} className="text-[10px] text-slate-500 hover:text-slate-400 transition-colors" data-testid={`deselect-all-${u.id}`}>Clear</button>
                          </div>
                        )}
                      </div>
                      <Input 
                        value={projectSearch} 
                        onChange={e => setProjectSearch(e.target.value)} 
                        placeholder="Search projects..." 
                        className="h-8 text-xs bg-slate-900 border-slate-700" 
                        data-testid={`project-search-${u.id}`} 
                      />
                      <div className="max-h-48 overflow-y-auto border border-slate-700 rounded-lg bg-slate-900">
                        {filteredProjects
                          .map(p => {
                            const isSelected = bulkSelection.has(p.id);
                            return (
                              <label key={p.id}
                                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b border-slate-800/50 last:border-0 ${isSelected ? "bg-blue-500/5" : "hover:bg-slate-800/50"}`}
                                data-testid={`bulk-project-${u.id}-${p.id}`}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleBulkSelect(p.id)}
                                  className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500/30 focus:ring-offset-0 accent-blue-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-medium text-slate-200">{p.name}</span>
                                  {p.client_name && <span className="text-[10px] text-slate-500 ml-2">— {p.client_name}</span>}
                                </div>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded border flex-shrink-0 ${
                                  p.status === "Draft" ? "border-slate-700 text-slate-500" :
                                  p.status === "Approved" ? "border-emerald-500/20 text-emerald-400 bg-emerald-500/10" :
                                  "border-blue-500/20 text-blue-400 bg-blue-500/10"
                                }`}>{p.status}</span>
                              </label>
                            );
                          })}
                        {projects.filter(p => !assignedIds.includes(p.id)).length === 0 && (
                          <p className="px-3 py-3 text-xs text-slate-500 text-center">All projects are already assigned to this user</p>
                        )}
                      </div>
                      {bulkSelection.size > 0 && (
                        <Button
                          onClick={() => bulkAssignProjects(u.id)}
                          disabled={bulkAssigning}
                          className="w-full bg-blue-600 hover:bg-blue-500 text-white text-xs h-8"
                          data-testid={`bulk-assign-btn-${u.id}`}
                        >
                          {bulkAssigning ? "Assigning..." : `Assign ${bulkSelection.size} Project${bulkSelection.size > 1 ? "s" : ""}`}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── Branding Tab (White-Label) ───
function BrandingTab({ branding, setBranding }) {
  const { updateBranding } = useBranding();
  const [form, setForm] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoRef = useRef(null);
  const faviconRef = useRef(null);

  useEffect(() => {
    if (branding && Object.keys(branding).length > 0) {
      setForm(prev => ({ ...prev, ...branding }));
    }
  }, [branding]);

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5MB"); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleFaviconFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Favicon must be under 2MB"); return; }
    setFaviconFile(file);
  };

  const uploadFile = async (file, endpoint) => {
    const formData = new FormData();
    formData.append("file", file);
    const r = await API.post(endpoint, formData, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const save = async () => {
    setSaving(true);
    try {
      let logoUrl = form.logo_url;
      let faviconUrl = form.favicon_url;

      if (logoFile) {
        setUploadingLogo(true);
        await uploadFile(logoFile, "/admin/branding/logo");
        logoUrl = `${BACKEND_URL}/api/admin/branding/logo?t=${Date.now()}`;
        setUploadingLogo(false);
        setLogoFile(null);
      }

      if (faviconFile) {
        setUploadingFavicon(true);
        await uploadFile(faviconFile, "/admin/branding/favicon");
        faviconUrl = `${BACKEND_URL}/api/admin/branding/favicon?t=${Date.now()}`;
        setUploadingFavicon(false);
        setFaviconFile(null);
      }

      const payload = { ...form, logo_url: logoUrl, favicon_url: faviconUrl };
      const r = await API.put("/admin/branding", payload);
      setBranding(r.data);
      updateBranding(r.data);
      toast.success("Branding settings saved — changes apply across the platform");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); setUploadingLogo(false); setUploadingFavicon(false); }
  };

  const currentLogoSrc = logoPreview || form.logo_url;

  return (
    <div className="space-y-6" data-testid="admin-branding-tab">
      {/* Platform Identity */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold">Platform Identity (White-Label)</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          <p className="text-xs text-slate-500 leading-relaxed">
            Customise the platform name, logo, and owner details. These values appear on the login page, navigation bar, and generated proposals.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Platform Name</Label>
              <Input value={form.platform_name || ""} onChange={e => setForm({ ...form, platform_name: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" placeholder="MEP" data-testid="branding-platform-name" />
              <p className="text-[10px] text-slate-600 mt-1">The bold part of the logo (e.g. "MEP")</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Platform Suffix</Label>
              <Input value={form.platform_suffix || ""} onChange={e => setForm({ ...form, platform_suffix: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" placeholder=".Engine" data-testid="branding-platform-suffix" />
              <p className="text-[10px] text-slate-600 mt-1">The lighter part of the logo (e.g. ".Engine")</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Owner / Company Name</Label>
              <Input value={form.owner_name || ""} onChange={e => setForm({ ...form, owner_name: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" placeholder="FURD Tech" data-testid="branding-owner-name" />
              <p className="text-[10px] text-slate-600 mt-1">Shown as "by [Owner]" in the navbar and login</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Company Logo</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="w-12 h-12 rounded-lg border border-slate-800 bg-slate-950 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {currentLogoSrc ? (
                    <img src={currentLogoSrc} alt="Logo" className="w-full h-full object-contain" data-testid="branding-logo-preview" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-slate-700" strokeWidth={1} />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoFile} className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} className="h-7 text-xs border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="branding-upload-logo-btn" disabled={uploadingLogo}>
                    <Upload className="w-3 h-3 mr-1" />{logoFile ? logoFile.name : "Upload logo"}
                  </Button>
                  <p className="text-[10px] text-slate-600">PNG, SVG, JPEG, WebP. Max 5MB.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={form.nav_show_owner !== false} onCheckedChange={(v) => setForm({ ...form, nav_show_owner: !!v })} />
            <label className="text-sm text-slate-400">Show owner name in navigation bar</label>
          </div>

          {/* Live preview */}
          <div className="p-4 bg-slate-950 rounded-lg border border-slate-800">
            <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Eye className="w-3 h-3" />Live Preview</p>
            <div className="flex items-center gap-1">
              {currentLogoSrc ? (
                <img src={currentLogoSrc} alt="Logo preview" className="h-7" />
              ) : (
                <>
                  <span className="font-heading text-lg font-extrabold tracking-tight text-blue-500">{form.platform_name || "MEP"}</span>
                  <span className="font-heading text-lg font-bold tracking-tight text-slate-200">{form.platform_suffix || ".Engine"}</span>
                </>
              )}
              {form.nav_show_owner !== false && form.owner_name && (
                <span className="text-[9px] text-slate-600 ml-2 font-medium tracking-wider uppercase">by {form.owner_name}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Login Page */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold">Login Page Text</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Login Title</Label>
              <Input value={form.login_title || ""} onChange={e => setForm({ ...form, login_title: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="branding-login-title" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Login Subtitle (optional)</Label>
              <Input value={form.login_subtitle || ""} onChange={e => setForm({ ...form, login_subtitle: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="branding-login-subtitle" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Proposal Branding */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold">Proposal Branding</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Company Name</Label>
              <Input value={form.company_name || ""} onChange={e => setForm({ ...form, company_name: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="branding-company-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Tagline</Label>
              <Input value={form.tagline || ""} onChange={e => setForm({ ...form, tagline: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="branding-tagline" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Proposal Header</Label>
              <Input value={form.proposal_header || ""} onChange={e => setForm({ ...form, proposal_header: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="branding-proposal-header" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Favicon</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input ref={faviconRef} type="file" accept="image/*,.ico" onChange={handleFaviconFile} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => faviconRef.current?.click()} className="h-7 text-xs border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="branding-upload-favicon-btn" disabled={uploadingFavicon}>
                  <Upload className="w-3 h-3 mr-1" />{faviconFile ? faviconFile.name : "Upload favicon"}
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Proposal Footer Text</Label>
            <textarea value={form.proposal_footer || ""} onChange={e => setForm({ ...form, proposal_footer: e.target.value })} rows={3} className="mt-1.5 w-full rounded-md bg-slate-900 border border-slate-800 px-3 py-2 text-sm" data-testid="branding-proposal-footer" />
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-xs">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Primary Colour</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="color" value={form.primary_color || "#3B82F6"} onChange={e => setForm({ ...form, primary_color: e.target.value })} className="w-8 h-8 rounded border border-slate-800 cursor-pointer" />
                <span className="text-xs font-mono text-slate-500">{form.primary_color}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Accent Colour</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="color" value={form.accent_color || "#3B82F6"} onChange={e => setForm({ ...form, accent_color: e.target.value })} className="w-8 h-8 rounded border border-slate-800 cursor-pointer" />
                <span className="text-xs font-mono text-slate-500">{form.accent_color}</span>
              </div>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-branding-btn">
            {saving ? (uploadingLogo ? "Uploading logo..." : uploadingFavicon ? "Uploading favicon..." : "Saving...") : "Save All Branding"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── System Tab ───
// ─── Calibration Toggle for Admin ───
function CalibrationToggleCard() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });

  useEffect(() => {
    API.get("/calibration/status").then(r => {
      setEnabled(r.data.enabled);
      setCounts(r.data.counts);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await API.post("/calibration/toggle");
      setEnabled(res.data.enabled);
      toast.success(`Calibration engine ${res.data.enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  if (loading) return <div className="h-8 bg-slate-800 rounded animate-pulse" />;

  return (
    <div data-testid="admin-calibration-toggle">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-400 leading-relaxed max-w-xl">
            When enabled, the calibration engine analyses historical estimated vs actual hours to generate multiplier adjustment recommendations. Directors can review and approve these from the Benchmarking → Calibration tab.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <span className={`text-xs font-medium ${enabled ? "text-emerald-500" : "text-slate-500"}`}>{enabled ? "Enabled" : "Disabled"}</span>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={toggling}
            data-testid="admin-calibration-switch"
            className={enabled ? "data-[state=checked]:bg-emerald-500" : ""}
          />
        </div>
      </div>
      {counts.total > 0 && (
        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
          <span>Recommendations: <span className="font-mono font-medium text-slate-300">{counts.total}</span></span>
          {counts.pending > 0 && <span>Pending: <span className="font-mono font-medium text-amber-500">{counts.pending}</span></span>}
          {counts.approved > 0 && <span>Approved: <span className="font-mono font-medium text-emerald-500">{counts.approved}</span></span>}
          {counts.rejected > 0 && <span>Rejected: <span className="font-mono font-medium text-rose-400">{counts.rejected}</span></span>}
        </div>
      )}
    </div>
  );
}

function SystemTab({ settings, setSettings }) {
  const confirm = useConfirm();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [resettingAudit, setResettingAudit] = useState(false);
  const [resettingProjects, setResettingProjects] = useState(false);
  const [resettingPricingHistory, setResettingPricingHistory] = useState(false);
  const [pricingHistoryStats, setPricingHistoryStats] = useState(null);
  const [factoryResetting, setFactoryResetting] = useState(false);
  const [factoryConfirmText, setFactoryConfirmText] = useState("");

  useEffect(() => {
    API.get("/admin/pricing-history/stats").then(r => setPricingHistoryStats(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await API.put("/admin/system", form);
      setSettings(r.data);
      toast.success("System settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const resetAudit = async () => {
    if (!(await confirm({ title: "Reset Audit Trail", description: "This will permanently delete all audit records. This cannot be undone.", confirmText: "Reset Audit Trail", variant: "danger" }))) return;
    setResettingAudit(true);
    try {
      const r = await API.post("/admin/reset/audit");
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setResettingAudit(false); }
  };

  const resetProjects = async () => {
    if (!(await confirm({ title: "Reset All Project Data", description: "This will permanently delete ALL projects, pricing, scope, proposals, risk assessments, pricing history, timesheets, and related data.\n\nThis is typically done before going live to ensure only real data is recorded for benchmarking.\n\nThis cannot be undone.", confirmText: "Reset All Projects", variant: "danger" }))) return;
    if (!(await confirm({ title: "Final Confirmation", description: "You are about to delete ALL project data. This action is irreversible.", confirmText: "Yes, Delete Everything", variant: "danger" }))) return;
    setResettingProjects(true);
    try {
      const r = await API.post("/admin/reset/projects");
      toast.success(r.data.message);
      setPricingHistoryStats({ snapshots: 0, benchmarks: 0, calibration: 0, total: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setResettingProjects(false); }
  };

  const resetPricingHistory = async () => {
    const count = pricingHistoryStats?.total || 0;
    if (!(await confirm({ title: "Reset Pricing History", description: `This will permanently delete:\n\n${pricingHistoryStats?.snapshots || 0} pricing snapshots (used by Quick Price Guidance)\n${pricingHistoryStats?.benchmarks || 0} benchmark records\n${pricingHistoryStats?.calibration || 0} calibration records\n\nTotal: ${count} records\n\nQuick Price Guidance and benchmarking will show no data until new projects are priced.\n\nThis cannot be undone.`, confirmText: "Reset Pricing History", variant: "danger" }))) return;
    setResettingPricingHistory(true);
    try {
      const r = await API.post("/admin/reset/pricing-history");
      toast.success(r.data.message);
      setPricingHistoryStats({ snapshots: 0, benchmarks: 0, calibration: 0, total: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setResettingPricingHistory(false); }
  };

  const factoryReset = async () => {
    if (factoryConfirmText !== "DELETE EVERYTHING") {
      toast.error("You must type DELETE EVERYTHING exactly to proceed.");
      return;
    }
    if (!(await confirm({ title: "FACTORY RESET — FINAL WARNING", description: "This will permanently delete:\n\n• ALL projects, scope, pricing, proposals\n• ALL users (including your account)\n• ALL admin settings, rates, templates\n• ALL timesheets, benchmarks, audit trail\n• ALL sessions and credentials\n\nThe platform will return to the first-user setup screen.\nThe first person to sign up becomes the new Director.\n\nTHIS CANNOT BE UNDONE.", confirmText: "Execute Factory Reset", variant: "danger" }))) return;
    setFactoryResetting(true);
    try {
      await API.post("/admin/factory-reset", { confirmation: "DELETE EVERYTHING" });
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/";
    } catch (e) { toast.error(e.response?.data?.detail || "Factory reset failed"); setFactoryResetting(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-system-tab">
      {/* Idle Timeout & Auto-Save */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold">Session Security</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          {/* Idle Timeout */}
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Inactivity Timeout</Label>
            <p className="text-[11px] text-slate-600 mt-0.5 mb-2">Automatically log out users after this many minutes of inactivity. Applies to all devices.</p>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={5}
                max={20}
                step={5}
                value={form.idle_timeout_minutes || 15}
                onChange={e => setForm({ ...form, idle_timeout_minutes: parseInt(e.target.value) })}
                className="flex-1 h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                data-testid="idle-timeout-slider"
              />
              <span className="text-lg font-mono font-bold text-blue-400 min-w-[4rem] text-center" data-testid="idle-timeout-value">
                {form.idle_timeout_minutes || 15} min
              </span>
            </div>
            <div className="flex justify-between text-[10px] text-slate-600 mt-1 px-0.5">
              <span>5 min</span><span>10 min</span><span>15 min</span><span>20 min</span>
            </div>
          </div>

          {/* Auto-Save Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-800">
            <div>
              <p className="text-sm font-medium text-slate-200">Auto-Save Progress</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Automatically save your work every {form.auto_save_interval_minutes || 5} minutes to prevent data loss before timeout.</p>
            </div>
            <button
              onClick={() => setForm({ ...form, auto_save_enabled: !form.auto_save_enabled })}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${form.auto_save_enabled !== false ? "bg-blue-600" : "bg-slate-700"}`}
              data-testid="auto-save-toggle"
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.auto_save_enabled !== false ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold">System Preferences</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Session Timeout (min)</Label><CellInput type="number" value={form.session_timeout_minutes || 480} onChange={e => setForm({ ...form, session_timeout_minutes: parseInt(e.target.value) || 480 })} className="mt-1.5 bg-slate-900 border-slate-800" /></div>
            <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Max File Upload (MB)</Label><CellInput type="number" value={form.max_file_upload_mb || 50} onChange={e => setForm({ ...form, max_file_upload_mb: parseInt(e.target.value) || 50 })} className="mt-1.5 bg-slate-900 border-slate-800" /></div>
          </div>
          <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-700/50 rounded-md px-4 py-3">
            <Mail className="w-4 h-4 text-blue-400 shrink-0" />
            <p className="text-xs text-slate-400">Email & SMTP settings have moved to the dedicated <strong className="text-slate-200">Email</strong> tab.</p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-slate-700 text-white" data-testid="save-system-btn">{saving ? "Saving..." : "Save System Settings"}</Button>
        </CardContent>
      </Card>

      {/* System Reset Controls */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">Pricing Engine Formula Version</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-mono font-bold text-blue-400" data-testid="formula-version-display">v1.6</span>
            <span className="text-xs text-slate-500">Last updated: 2026-09-09</span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            Each pricing calculation records this formula version. If formulas change in the future, older projects will still reference the version used at the time. View the full changelog in the User Manual &rarr; Formula Version History.
          </p>
          <div className="border-t border-slate-800 pt-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Recent Changes (v1.6)</h4>
            <ul className="space-y-1 text-xs text-slate-400">
              <li>• "General" discipline integrated into fee-by-discipline breakdown</li>
              <li>• Staff Hours CSV template download and bulk upload</li>
              <li>• httpOnly cookie-based JWT for improved session security</li>
              <li>• Performance improvements via memoised admin computations</li>
            </ul>
          </div>
          <div className="border-t border-slate-800 pt-3 mt-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">v1.5 Changes</h4>
            <ul className="space-y-1 text-xs text-slate-400">
              <li>• Manual Fee Overrides at Total, Stage, and Stage+Discipline levels</li>
              <li>• Granular Discounts (%, fixed) at Project, Stage, Stage+Discipline levels</li>
              <li>• Admin-managed multi-select technical systems dropdown</li>
              <li>• Sub-deliverables for detailed scope breakdowns</li>
              <li>• CSV Import/Export with Replace and Merge modes</li>
              <li>• Scope Template Save/Load with merge comparison preview</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Calibration Engine Toggle */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">Pricing Calibration Engine</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <CalibrationToggleCard />
        </CardContent>
      </Card>

      {/* System Reset Controls — Danger Zone */}
      <Card className="bg-slate-900 border border-rose-500/20 shadow-sm">
        <CardHeader className="border-b border-rose-500/20 bg-rose-500/5 py-3 px-4">
          <CardTitle className="text-sm font-semibold text-rose-400">System Reset Controls</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-6">
          <p className="text-xs text-slate-400 leading-relaxed">
            Use these controls to reset system data. This is typically needed before going live to ensure only real project data is recorded for benchmarking and pricing intelligence. These actions are permanent and cannot be undone.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border border-slate-800 bg-slate-950">
              <h4 className="text-sm font-semibold text-slate-200">Reset Audit Trail</h4>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Clears all audit trail records (scope changes, pricing overrides, approval decisions, system events). Does not affect project data.</p>
              <Button variant="destructive" size="sm" onClick={resetAudit} disabled={resettingAudit} data-testid="reset-audit-btn" className="mt-3">
                <Trash2 className="w-3 h-3 mr-1.5" />{resettingAudit ? "Resetting..." : "Reset Audit Trail"}
              </Button>
            </div>
            <div className="p-4 rounded-lg border border-amber-500/20 bg-amber-500/5">
              <h4 className="text-sm font-semibold text-amber-400">Reset Pricing History</h4>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Clears pricing snapshots, benchmarks, and calibration data. Quick Price Guidance will start fresh. Project data is preserved.</p>
              {pricingHistoryStats && pricingHistoryStats.total > 0 && (
                <p className="text-[10px] font-mono text-amber-500/80 mt-1.5">{pricingHistoryStats.snapshots} snapshots, {pricingHistoryStats.benchmarks} benchmarks, {pricingHistoryStats.calibration} calibration records</p>
              )}
              <Button variant="destructive" size="sm" onClick={resetPricingHistory} disabled={resettingPricingHistory || (pricingHistoryStats && pricingHistoryStats.total === 0)} data-testid="reset-pricing-history-btn" className="mt-3">
                <Trash2 className="w-3 h-3 mr-1.5" />{resettingPricingHistory ? "Resetting..." : pricingHistoryStats?.total === 0 ? "No History Data" : "Reset Pricing History"}
              </Button>
            </div>
            <div className="p-4 rounded-lg border border-rose-500/20 bg-rose-500/5">
              <h4 className="text-sm font-semibold text-rose-400">Reset All Project Data</h4>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Permanently deletes ALL projects, scope, pricing, proposals, risk assessments, timesheets, pricing history, and notifications. User accounts and admin settings are preserved.</p>
              <Button variant="destructive" size="sm" onClick={resetProjects} disabled={resettingProjects} data-testid="reset-projects-btn" className="mt-3">
                <Trash2 className="w-3 h-3 mr-1.5" />{resettingProjects ? "Resetting..." : "Reset All Projects"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orphan Cleanup */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="orphan-cleanup-card">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-bold uppercase tracking-wider">Database Cleanup</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <p className="text-xs text-slate-400 leading-relaxed">
            Remove orphaned records left behind by previously deleted projects — pricing requests, snapshots, timesheets, etc. that reference projects that no longer exist. This is safe and non-destructive to active data.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await API.post("/admin/cleanup-orphans");
                const d = res.data;
                if (Object.keys(d.details || {}).length === 0) {
                  toast.success("No orphaned records found — database is clean");
                } else {
                  toast.success(`Cleaned ${d.message}`);
                }
              } catch { toast.error("Failed to run cleanup"); }
            }}
            className="border-slate-700 text-xs"
            data-testid="cleanup-orphans-btn"
          >
            Run Cleanup
          </Button>
        </CardContent>
      </Card>

      {/* Factory Reset — Nuclear Option */}
      <Card className="bg-slate-900 border-2 border-rose-600/40 shadow-sm" data-testid="factory-reset-card">
        <CardHeader className="border-b border-rose-600/30 bg-rose-600/10 py-3 px-4">
          <CardTitle className="text-sm font-bold text-rose-500 uppercase tracking-wider">Factory Reset — Delete Everything</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <p className="text-xs text-rose-400/80 leading-relaxed font-medium">
            This will wipe ALL data from the platform — every project, every user (including you), all settings, all history. The platform returns to the first-user setup screen. The first person to create an account becomes the new Director.
          </p>
          <div className="p-4 rounded-lg border border-rose-600/30 bg-rose-950/50 space-y-3">
            <Label className="text-xs text-rose-400 uppercase tracking-wider font-semibold">Type DELETE EVERYTHING to enable the button</Label>
            <Input
              value={factoryConfirmText}
              onChange={e => setFactoryConfirmText(e.target.value)}
              placeholder="DELETE EVERYTHING"
              className="bg-slate-950 border-rose-600/30 text-rose-300 placeholder:text-rose-800 font-mono text-sm max-w-sm"
              data-testid="factory-reset-confirm-input"
            />
            <Button
              variant="destructive"
              size="sm"
              onClick={factoryReset}
              disabled={factoryResetting || factoryConfirmText !== "DELETE EVERYTHING"}
              data-testid="factory-reset-btn"
              className="bg-rose-700 hover:bg-rose-600 disabled:opacity-30"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              {factoryResetting ? "Resetting..." : "Execute Factory Reset"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── GDPR / Data Protection Tab ───
function GDPRTab({ gdpr, setGdpr }) {
  const [form, setForm] = useState(gdpr);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await API.put("/admin/gdpr", form);
      setGdpr(r.data);
      toast.success("Data protection settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-gdpr-tab">
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold">Data Protection (GDPR)</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Data Retention (days)</Label><CellInput type="number" value={form.data_retention_days || 1095} onChange={e => setForm({ ...form, data_retention_days: parseInt(e.target.value) || 1095 })} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="gdpr-retention" /></div>
            <div><Label className="text-xs text-slate-500 uppercase tracking-wider">DPO Email</Label><Input value={form.dpo_email || ""} onChange={e => setForm({ ...form, dpo_email: e.target.value })} placeholder="dpo@company.com" className="mt-1.5 bg-slate-900 border-slate-800" data-testid="gdpr-dpo-email" /></div>
            <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Data Processor Name</Label><Input value={form.data_processor_name || ""} onChange={e => setForm({ ...form, data_processor_name: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="gdpr-processor" /></div>
            <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Data Processor Contact</Label><Input value={form.data_processor_contact || ""} onChange={e => setForm({ ...form, data_processor_contact: e.target.value })} className="mt-1.5 bg-slate-900 border-slate-800" /></div>
          </div>
          <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Privacy Notice URL</Label><Input value={form.privacy_notice_url || ""} onChange={e => setForm({ ...form, privacy_notice_url: e.target.value })} placeholder="https://..." className="mt-1.5 bg-slate-900 border-slate-800 max-w-lg" /></div>
          <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Consent Text</Label><textarea value={form.consent_text || ""} onChange={e => setForm({ ...form, consent_text: e.target.value })} rows={3} className="mt-1.5 w-full rounded-md bg-slate-900 border border-slate-800 px-3 py-2 text-sm" /></div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.auto_anonymise_closed_projects || false} onChange={e => setForm({ ...form, auto_anonymise_closed_projects: e.target.checked })} className="rounded border-slate-800" />Auto-anonymise closed projects</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.export_enabled !== false} onChange={e => setForm({ ...form, export_enabled: e.target.checked })} className="rounded border-slate-800" />Allow data export</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.deletion_request_enabled !== false} onChange={e => setForm({ ...form, deletion_request_enabled: e.target.checked })} className="rounded border-slate-800" />Allow deletion requests</label>
          </div>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-slate-700 text-white" data-testid="save-gdpr-btn">{saving ? "Saving..." : "Save Data Protection Settings"}</Button>
        </CardContent>
      </Card>
    </div>
  );
}


// ─── Email Configuration Tab ───
function EmailTab() {
  const [smtp, setSmtp] = useState({ provider: "smtp", host: "", port: 587, user: "", password: "", from_email: "noreply@mep-engine.com", use_tls: true, enabled: false, enabled_events: {}, resend_api_key: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [eventTypes, setEventTypes] = useState({});
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewEvent, setPreviewEvent] = useState("");
  const [activeSection, setActiveSection] = useState("monitoring");
  const [editingPassword, setEditingPassword] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState(false);

  useEffect(() => {
    API.get("/admin/smtp").then(r => {
      const d = r.data;
      setSmtp({
        provider: d.provider || "smtp",
        host: d.host || "", port: d.port || 587, user: d.user || "", password: "",
        from_email: d.from_email || "noreply@mep-engine.com", use_tls: d.use_tls !== false,
        enabled: d.enabled || false, enabled_events: d.enabled_events || {},
        has_password: d.has_password || false, password_masked: d.password_masked || "",
        resend_api_key: "", has_resend_key: d.has_resend_key || false, resend_api_key_masked: d.resend_api_key_masked || "",
      });
      setEventTypes(d.event_types || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveSmtp = async () => {
    setSaving(true);
    try {
      const payload = { provider: smtp.provider, host: smtp.host, port: smtp.port, user: smtp.user, password: smtp.password, from_email: smtp.from_email, use_tls: smtp.use_tls, enabled: smtp.enabled, enabled_events: smtp.enabled_events, resend_api_key: smtp.resend_api_key };
      const r = await API.put("/admin/smtp", payload);
      setSmtp(prev => ({ ...prev, has_password: r.data.has_password, password_masked: r.data.password_masked, password: "", has_resend_key: r.data.has_resend_key, resend_api_key_masked: r.data.resend_api_key_masked, resend_api_key: "" }));
      setEditingPassword(false);
      setEditingApiKey(false);
      toast.success("Email settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try {
      const r = await API.post("/admin/smtp/test", { to_email: testEmail });
      toast.success(r.data.message || "Test email sent!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send test email"); }
    finally { setTesting(false); }
  };

  const loadPreview = async (eventType) => {
    setPreviewEvent(eventType);
    try {
      const r = await API.post("/admin/smtp/preview", { event_type: eventType });
      setPreviewHtml(r.data.html || "");
    } catch { toast.error("Failed to load preview"); }
  };

  const toggleEvent = (key) => {
    setSmtp(prev => ({ ...prev, enabled_events: { ...prev.enabled_events, [key]: !(prev.enabled_events[key] !== false) } }));
  };

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-48 bg-slate-800 rounded-lg" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-email-tab">
      {/* Section nav */}
      <div className="flex flex-wrap gap-1 bg-slate-900 border border-slate-800 rounded-lg p-1 w-fit" data-testid="email-section-nav">
        {[
          { id: "monitoring", label: "Monitoring", icon: BarChart3Icon },
          { id: "config", label: "Email Config", icon: Server },
          { id: "events", label: "Event Types", icon: Mail },
          { id: "logs", label: "Delivery Log", icon: RefreshCw },
          { id: "preview", label: "Template Preview", icon: Eye },
        ].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeSection === s.id ? "bg-slate-800 text-white shadow-sm" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"}`}
            data-testid={`email-section-${s.id}`}>
            <s.icon className="w-3.5 h-3.5" />{s.label}
          </button>
        ))}
      </div>

      {/* Monitoring Dashboard */}
      {activeSection === "monitoring" && <EmailMonitoringDashboard />}

      {/* Email Configuration */}
      {activeSection === "config" && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Server className="w-4 h-4 text-blue-400" />Email Provider Configuration</CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">Email Notifications</span>
              <Switch checked={smtp.enabled} onCheckedChange={(v) => setSmtp(prev => ({ ...prev, enabled: v }))} data-testid="smtp-enabled-toggle" />
            </div>
          </CardHeader>
          <CardContent className="p-5 space-y-5">
            {!smtp.enabled && (
              <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-md px-4 py-3">
                <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                <p className="text-sm text-amber-300">Email notifications are currently disabled. Enable the toggle above to start sending emails.</p>
              </div>
            )}

            {/* Provider Selection */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Email Provider</Label>
              <div className="flex gap-2" data-testid="provider-selection">
                {[
                  { id: "smtp", label: "SMTP", desc: "Direct SMTP server (Gmail, Outlook, custom)" },
                  { id: "resend", label: "Resend", desc: "Transactional email API — recommended for production" },
                ].map(p => (
                  <button key={p.id} onClick={() => setSmtp(prev => ({ ...prev, provider: p.id }))}
                    className={`flex-1 text-left border rounded-lg p-3.5 transition-all ${smtp.provider === p.id ? "border-blue-500/50 bg-blue-500/5 ring-1 ring-blue-500/20" : "border-slate-800 hover:border-slate-700 hover:bg-slate-800/30"}`}
                    data-testid={`provider-${p.id}`}>
                    <p className={`text-sm font-semibold ${smtp.provider === p.id ? "text-blue-400" : "text-slate-300"}`}>{p.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{p.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* SMTP Fields */}
            {smtp.provider === "smtp" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">SMTP Host</Label>
                  <Input value={smtp.host} onChange={e => setSmtp(p => ({ ...p, host: e.target.value }))} placeholder="smtp.office365.com" className="mt-1.5 bg-slate-900 border-slate-800 font-mono text-sm" data-testid="smtp-host" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">Port</Label>
                  <CellInput type="number" value={smtp.port} onChange={e => setSmtp(p => ({ ...p, port: parseInt(e.target.value) || 587 }))} className="mt-1.5 bg-slate-900 border-slate-800 font-mono text-sm" data-testid="smtp-port" />
                  <p className="text-[11px] text-slate-600 mt-1">587 (TLS) or 465 (SSL) or 25 (unencrypted)</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">Username / Email</Label>
                  <Input value={smtp.user} onChange={e => setSmtp(p => ({ ...p, user: e.target.value }))} placeholder="your@email.com" className="mt-1.5 bg-slate-900 border-slate-800 text-sm" data-testid="smtp-user" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">Password / App Password</Label>
                  {smtp.has_password && !editingPassword ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-md">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span className="text-sm text-slate-300 font-mono">{smtp.password_masked || "••••••••"}</span>
                        <span className="text-[10px] text-emerald-400 font-medium ml-1">Saved</span>
                      </div>
                      <Button variant="outline" size="sm" className="h-9 text-xs border-slate-700 hover:bg-slate-800 shrink-0"
                        onClick={() => { setEditingPassword(true); setSmtp(p => ({ ...p, password: "" })); }}
                        data-testid="change-password-btn">
                        <Edit2 className="w-3 h-3 mr-1" />Change
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-1.5 flex items-center gap-2">
                      <Input type="password" value={smtp.password} onChange={e => setSmtp(p => ({ ...p, password: e.target.value }))} placeholder="Enter new password" className="bg-slate-900 border-slate-800 text-sm flex-1" data-testid="smtp-password" autoFocus={editingPassword} />
                      {editingPassword && (
                        <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-400 hover:text-slate-200 shrink-0"
                          onClick={() => { setEditingPassword(false); setSmtp(p => ({ ...p, password: "" })); }}
                          data-testid="cancel-password-btn">
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">From Email Address</Label>
                  <Input value={smtp.from_email} onChange={e => setSmtp(p => ({ ...p, from_email: e.target.value }))} placeholder="noreply@company.com" className="mt-1.5 bg-slate-900 border-slate-800 text-sm" data-testid="smtp-from" />
                </div>
                <div className="flex items-end gap-3 pb-1">
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <Switch checked={smtp.use_tls} onCheckedChange={(v) => setSmtp(prev => ({ ...prev, use_tls: v }))} data-testid="smtp-tls-toggle" />
                    Use TLS / STARTTLS
                  </label>
                </div>
              </div>
            )}

            {/* Resend Fields */}
            {smtp.provider === "resend" && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Resend API Key</Label>
                    {smtp.has_resend_key && !editingApiKey ? (
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-md">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          <span className="text-sm text-slate-300 font-mono">{smtp.resend_api_key_masked || "re_••••••••••••"}</span>
                          <span className="text-[10px] text-emerald-400 font-medium ml-1">Saved</span>
                        </div>
                        <Button variant="outline" size="sm" className="h-9 text-xs border-slate-700 hover:bg-slate-800 shrink-0"
                          onClick={() => { setEditingApiKey(true); setSmtp(p => ({ ...p, resend_api_key: "" })); }}
                          data-testid="change-api-key-btn">
                          <Edit2 className="w-3 h-3 mr-1" />Change
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-1.5 flex items-center gap-2">
                        <Input type="password" value={smtp.resend_api_key} onChange={e => setSmtp(p => ({ ...p, resend_api_key: e.target.value }))} placeholder="re_..." className="bg-slate-900 border-slate-800 font-mono text-sm flex-1" data-testid="resend-api-key" autoFocus={editingApiKey} />
                        {editingApiKey && (
                          <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-400 hover:text-slate-200 shrink-0"
                            onClick={() => { setEditingApiKey(false); setSmtp(p => ({ ...p, resend_api_key: "" })); }}
                            data-testid="cancel-api-key-btn">
                            Cancel
                          </Button>
                        )}
                      </div>
                    )}
                    <p className="text-[11px] text-slate-600 mt-1">Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline">resend.com/api-keys</a></p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">From Email Address</Label>
                    <Input value={smtp.from_email} onChange={e => setSmtp(p => ({ ...p, from_email: e.target.value }))} placeholder="noreply@yourdomain.com" className="mt-1.5 bg-slate-900 border-slate-800 text-sm" data-testid="resend-from" />
                    <p className="text-[11px] text-slate-600 mt-1">Use <span className="font-mono text-slate-500">onboarding@resend.dev</span> for testing, or your verified domain</p>
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-300 mb-2">Quick Setup</h4>
                  <ol className="text-[11px] text-slate-500 space-y-1.5 list-decimal list-inside">
                    <li>Sign up at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline">resend.com</a> (free tier: 100 emails/day)</li>
                    <li>Go to Dashboard &rarr; API Keys &rarr; Create API Key</li>
                    <li>Paste the key above (starts with <span className="font-mono text-slate-400">re_</span>)</li>
                    <li>For production: Add and verify your domain under Domains</li>
                    <li>Use <span className="font-mono text-slate-400">noreply@yourdomain.com</span> as the sender</li>
                  </ol>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={saveSmtp} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-smtp-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? "Saving..." : "Save Email Settings"}
              </Button>
            </div>

            {/* Test Email */}
            <div className="border-t border-slate-800 pt-5 mt-5">
              <h4 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-blue-400" />Send Test Email</h4>
              <div className="flex gap-2 max-w-lg">
                <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="recipient@example.com" className="bg-slate-900 border-slate-800 text-sm flex-1" data-testid="test-email-input" />
                <Button onClick={sendTest} disabled={testing} variant="outline" className="border-slate-700 hover:bg-slate-800 text-sm shrink-0" data-testid="send-test-email-btn">
                  <Send className="w-3.5 h-3.5 mr-1.5" />{testing ? "Sending..." : "Send Test"}
                </Button>
              </div>
              <p className="text-[11px] text-slate-600 mt-1.5">Sends a test email via {smtp.provider === "resend" ? "Resend API" : "SMTP"} to verify your configuration.</p>
            </div>

            {/* Quick setup guides (SMTP only) */}
            {smtp.provider === "smtp" && (
              <div className="border-t border-slate-800 pt-5 mt-5">
                <h4 className="text-sm font-semibold text-slate-200 mb-3">Quick Setup Guides</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { name: "Gmail", host: "smtp.gmail.com", port: 587, note: "Requires App Password (enable 2FA first in Google Account > Security)", slug: "gmail" },
                    { name: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587, note: "Enable SMTP AUTH on your mailbox, or use App Password with 2FA", slug: "outlook" },
                    { name: "Custom SMTP", host: "", port: 587, note: "Enter your SMTP server details", slug: "custom" },
                  ].map(guide => (
                    <button key={guide.name} onClick={() => { if (guide.host) setSmtp(p => ({ ...p, host: guide.host, port: guide.port })); }}
                      className="text-left border border-slate-800 rounded-md p-3 hover:border-slate-700 hover:bg-slate-800/30 transition-colors"
                      data-testid={`smtp-guide-${guide.slug}`}>
                      <p className="text-sm font-medium text-slate-200">{guide.name}</p>
                      {guide.host && <p className="text-xs font-mono text-slate-500 mt-0.5">{guide.host}:{guide.port}</p>}
                      <p className="text-[11px] text-slate-600 mt-1">{guide.note}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

            {/* Resend Webhook Setup */}
            {smtp.provider === "resend" && (
              <div className="border-t border-slate-800 pt-5 mt-5">
                <h4 className="text-sm font-semibold text-slate-200 mb-2">Delivery Tracking Webhook</h4>
                <p className="text-xs text-slate-500 mb-3">Enable delivery tracking to see when emails are delivered, bounced, or opened. This creates a webhook in your Resend account.</p>
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      const r = await API.post("/admin/email/setup-webhook");
                      if (r.data.already_exists) {
                        toast.info("Webhook already configured");
                      } else {
                        toast.success("Delivery tracking webhook created");
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to create webhook");
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-500 text-white h-9"
                  data-testid="setup-resend-webhook-btn"
                >
                  Enable Delivery Tracking
                </Button>
              </div>
            )}

      {/* Event Types */}
      {activeSection === "events" && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-blue-400" />Notification Event Types</CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-1">
            <p className="text-xs text-slate-500 mb-4">Choose which events trigger email notifications. All events are enabled by default.</p>
            {Object.entries(eventTypes).map(([key, meta]) => {
              const enabled = smtp.enabled_events[key] !== false;
              return (
                <div key={key} className="flex items-center justify-between py-3 border-b border-slate-800/50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-200">{meta.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{meta.description}</p>
                  </div>
                  <Switch checked={enabled} onCheckedChange={() => toggleEvent(key)} data-testid={`event-toggle-${key}`} />
                </div>
              );
            })}
            <div className="pt-4">
              <Button onClick={saveSmtp} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-events-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? "Saving..." : "Save Event Settings"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery Log */}
      {activeSection === "logs" && <EmailDeliveryLog />}

      {/* Template Preview */}
      {activeSection === "preview" && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-blue-400" />Email Template Preview</CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <p className="text-xs text-slate-500">Preview how email notifications will appear to recipients.</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(eventTypes).map(([key, meta]) => (
                <Button key={key} variant={previewEvent === key ? "default" : "outline"} size="sm"
                  className={`text-xs h-8 ${previewEvent === key ? "bg-blue-600 text-white" : "border-slate-700 hover:bg-slate-800"}`}
                  onClick={() => loadPreview(key)} data-testid={`preview-btn-${key}`}>
                  {meta.label}
                </Button>
              ))}
            </div>
            {previewHtml && (
              <div className="border border-slate-700 rounded-lg overflow-hidden mt-4">
                <div className="bg-slate-800/50 px-4 py-2 flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-mono">Preview: {eventTypes[previewEvent]?.label}</span>
                </div>
                <div className="bg-white p-4" style={{ minHeight: "200px" }}>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} />
                </div>
              </div>
            )}
            {!previewHtml && (
              <div className="border border-slate-800 border-dashed rounded-lg p-8 text-center text-slate-600 text-sm">
                Select an event type above to preview its email template.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Email Monitoring Dashboard ───
function EmailMonitoringDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsR, healthR] = await Promise.all([
        API.get("/admin/email/monitoring/stats?days=14"),
        API.get("/admin/email/monitoring/health"),
      ]);
      setStats(statsR.data);
      setHealth(healthR.data);
    } catch { toast.error("Failed to load monitoring data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-48 bg-slate-800 rounded-lg" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-800 rounded-lg" />)}</div></div>;

  const t = stats?.totals || {};
  const timeline = stats?.daily_timeline || [];
  const maxDayVol = Math.max(1, ...timeline.map(d => d.sent + d.failed));
  const eventBreakdown = stats?.event_breakdown || {};
  const errorCats = stats?.error_categories || {};
  const sortedErrorCats = Object.entries(errorCats).sort((a, b) => b[1] - a[1]);
  const providerBreakdown = stats?.provider_breakdown || {};
  const hourlyHeatmap = stats?.hourly_heatmap || {};

  const HEALTH_LABELS = { healthy: "Healthy", degraded: "Degraded", critical: "Critical", disabled: "Disabled", not_configured: "Not Configured", no_activity: "No Activity" };
  const HEALTH_STYLES = { green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", amber: "bg-amber-500/15 text-amber-400 border-amber-500/30", red: "bg-rose-500/15 text-rose-400 border-rose-500/30", gray: "bg-slate-500/15 text-slate-400 border-slate-500/30" };

  const EVENT_LABELS = {
    approval_status_change: "Approvals", project_assignment: "Assignments", proposal_issued: "Proposals",
    user_invited: "Invitations", timesheet_submitted: "Timesheets", timesheet_approved: "Timesheet Reviews",
    password_reset: "Password Reset", test: "Test", unknown: "Other"
  };

  const ERROR_COLORS = { Authentication: "bg-rose-500", Connection: "bg-amber-500", "Rate Limit": "bg-purple-500", DNS: "bg-sky-500", Other: "bg-slate-500" };
  const totalErrors = Object.values(errorCats).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-5" data-testid="email-monitoring-dashboard">
      {/* Health + KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {/* Health status card */}
        <div className={`rounded-lg border p-4 ${HEALTH_STYLES[health?.health_color || "gray"]}`} data-testid="email-health-card">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-widest opacity-70">System Health</span>
            <button onClick={fetchData} className="opacity-60 hover:opacity-100 transition-opacity" data-testid="refresh-monitoring-btn"><RefreshCw className="w-3 h-3" /></button>
          </div>
          <p className="text-lg font-bold">{HEALTH_LABELS[health?.health] || "Unknown"}</p>
          <p className="text-[11px] opacity-70 mt-1">{health?.provider?.toUpperCase()} &middot; {health?.is_enabled ? "Enabled" : "Disabled"}</p>
          {health?.last_success_at && <p className="text-[10px] opacity-50 mt-1">Last OK: {new Date(health.last_success_at).toLocaleDateString()}</p>}
        </div>

        {/* Total Sent */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="kpi-total-sent">
          <div className="flex items-center gap-1.5 mb-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /><span className="text-[10px] uppercase tracking-widest text-slate-500">Total Sent</span></div>
          <p className="text-2xl font-bold text-emerald-400 font-mono">{t.sent?.toLocaleString() || 0}</p>
          <p className="text-[10px] text-slate-600 mt-1">{stats?.recent_24h?.sent || 0} in last 24h</p>
        </div>

        {/* Total Failed */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="kpi-total-failed">
          <div className="flex items-center gap-1.5 mb-1"><XCircle className="w-3.5 h-3.5 text-rose-400" /><span className="text-[10px] uppercase tracking-widest text-slate-500">Total Failed</span></div>
          <p className="text-2xl font-bold text-rose-400 font-mono">{t.failed?.toLocaleString() || 0}</p>
          <p className="text-[10px] text-slate-600 mt-1">{stats?.recent_24h?.failed || 0} in last 24h</p>
        </div>

        {/* Success Rate */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="kpi-success-rate">
          <div className="flex items-center gap-1.5 mb-1"><TrendingUpIcon className="w-3.5 h-3.5 text-blue-400" /><span className="text-[10px] uppercase tracking-widest text-slate-500">Success Rate</span></div>
          <p className={`text-2xl font-bold font-mono ${t.success_rate >= 95 ? "text-emerald-400" : t.success_rate >= 80 ? "text-amber-400" : "text-rose-400"}`}>{t.success_rate || 0}%</p>
          <div className="w-full h-1.5 bg-slate-800 rounded-full mt-2 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${t.success_rate >= 95 ? "bg-emerald-500" : t.success_rate >= 80 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${t.success_rate || 0}%` }} />
          </div>
        </div>

        {/* Avg Daily */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="kpi-avg-daily">
          <div className="flex items-center gap-1.5 mb-1"><ClockIcon className="w-3.5 h-3.5 text-violet-400" /><span className="text-[10px] uppercase tracking-widest text-slate-500">Avg Daily</span></div>
          <p className="text-2xl font-bold text-violet-400 font-mono">{stats?.avg_daily_volume || 0}</p>
          <p className="text-[10px] text-slate-600 mt-1">emails / day (14d avg)</p>
        </div>
      </div>

      {/* Delivery Timeline Chart */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <BarChart3Icon className="w-4 h-4 text-blue-400" />Delivery Timeline
            <span className="text-[10px] text-slate-500 font-normal ml-2">Last 14 days</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4" data-testid="delivery-timeline-chart">
          {timeline.length > 0 ? (
            <div className="flex items-end gap-[3px] h-36">
              {timeline.map((d, i) => {
                const total = d.sent + d.failed;
                const sentH = total > 0 ? (d.sent / maxDayVol) * 100 : 0;
                const failedH = total > 0 ? (d.failed / maxDayVol) * 100 : 0;
                const dayLabel = d.date.slice(5);
                return (
                  <div key={d.date || `timeline-${i}`} className="flex-1 flex flex-col items-center gap-0.5 group relative" data-testid={`timeline-bar-${i}`}>
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      {d.date}: {d.sent} sent, {d.failed} failed
                    </div>
                    <div className="w-full flex flex-col justify-end" style={{ height: "120px" }}>
                      {d.failed > 0 && <div className="w-full bg-rose-500/80 rounded-t-[2px] transition-all" style={{ height: `${failedH}%`, minHeight: d.failed > 0 ? "2px" : 0 }} />}
                      <div className="w-full bg-emerald-500/80 rounded-t-[2px] transition-all" style={{ height: `${sentH}%`, minHeight: d.sent > 0 ? "2px" : 0 }} />
                    </div>
                    <span className="text-[9px] text-slate-600 mt-0.5">{i % 2 === 0 ? dayLabel : ""}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-36 flex items-center justify-center text-slate-600 text-sm">No data available</div>
          )}
          <div className="flex items-center gap-4 mt-3 justify-center">
            <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/80" />Sent</span>
            <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500/80" />Failed</span>
          </div>
        </CardContent>
      </Card>

      {/* Bottom grid: Event Breakdown + Error Categories + Hourly Heatmap */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Event Type Breakdown */}
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <CardTitle className="text-xs font-semibold flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400" />By Event Type</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2" data-testid="event-breakdown">
            {Object.keys(eventBreakdown).length > 0 ? Object.entries(eventBreakdown).sort((a, b) => (b[1].sent + b[1].failed) - (a[1].sent + a[1].failed)).map(([evt, counts]) => {
              const total = (counts.sent || 0) + (counts.failed || 0);
              const pct = t.total > 0 ? Math.round(total / t.total * 100) : 0;
              return (
                <div key={evt} className="flex items-center gap-3" data-testid={`event-row-${evt}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-slate-300 truncate">{EVENT_LABELS[evt] || evt}</span>
                      <span className="text-[10px] text-slate-500 font-mono ml-2">{total}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-800 rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-blue-500/60 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            }) : <p className="text-xs text-slate-600 text-center py-4">No events recorded</p>}
          </CardContent>
        </Card>

        {/* Error Categories */}
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <CardTitle className="text-xs font-semibold flex items-center gap-2"><AlertCircle className="w-3.5 h-3.5 text-rose-400" />Failure Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="error-breakdown">
            {Object.keys(errorCats).length > 0 ? (
              <div className="space-y-3">
                {/* Segmented bar */}
                <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden flex">
                  {Object.entries(errorCats).map(([cat, count]) => (
                    <div key={cat} className={`h-full ${ERROR_COLORS[cat] || "bg-slate-500"} transition-all`} style={{ width: `${(count / totalErrors) * 100}%` }} title={`${cat}: ${count}`} />
                  ))}
                </div>
                {/* Legend */}
                <div className="space-y-1.5">
                  {sortedErrorCats.map(([cat, count]) => (
                    <div key={cat} className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-xs text-slate-300">
                        <span className={`w-2 h-2 rounded-full ${ERROR_COLORS[cat] || "bg-slate-500"}`} />{cat}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500">{count} ({Math.round(count / totalErrors * 100)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-xs text-slate-600 text-center py-4">No failures recorded</p>}
          </CardContent>
        </Card>

        {/* Hourly Heatmap */}
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <CardTitle className="text-xs font-semibold flex items-center gap-2"><ClockIcon className="w-3.5 h-3.5 text-violet-400" />Hourly Distribution</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="hourly-heatmap">
            {Object.keys(hourlyHeatmap).length > 0 ? (() => {
              const maxH = Math.max(1, ...Object.values(hourlyHeatmap));
              return (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-1">
                    {Array.from({ length: 24 }, (_, h) => {
                      const count = hourlyHeatmap[String(h)] || 0;
                      const intensity = count / maxH;
                      return (
                        <div key={h} className="group relative flex flex-col items-center">
                          <div className="w-full aspect-square rounded-sm transition-colors" style={{ backgroundColor: intensity > 0 ? `rgba(59, 130, 246, ${0.15 + intensity * 0.75})` : "rgb(30, 41, 59)" }} />
                          {h % 4 === 0 && <span className="text-[8px] text-slate-600 mt-0.5">{String(h).padStart(2, "0")}</span>}
                          <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-[9px] text-slate-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            {String(h).padStart(2, "0")}:00 — {count} emails
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between text-[9px] text-slate-600 mt-1">
                    <span>Low</span>
                    <div className="flex gap-0.5">{[0.15, 0.3, 0.5, 0.7, 0.9].map((op, i) => <div key={`op-${op}`} className="w-3 h-2 rounded-sm" style={{ backgroundColor: `rgba(59, 130, 246, ${op})` }} />)}</div>
                    <span>High</span>
                  </div>
                </div>
              );
            })() : <p className="text-xs text-slate-600 text-center py-4">No activity data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Provider Breakdown */}
      {Object.keys(providerBreakdown).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(providerBreakdown).map(([prov, counts]) => {
            const total = (counts.sent || 0) + (counts.failed || 0);
            const rate = total > 0 ? Math.round((counts.sent || 0) / total * 100) : 0;
            return (
              <div key={prov} className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex items-center gap-4" data-testid={`provider-stat-${prov}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                  <Server className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-200">{prov === "resend" ? "Resend API" : "SMTP"}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] text-emerald-400">{counts.sent || 0} sent</span>
                    <span className="text-[10px] text-rose-400">{counts.failed || 0} failed</span>
                    <span className="text-[10px] text-slate-500">{rate}% success</span>
                  </div>
                </div>
                <div className="w-12 h-12 relative">
                  <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgb(30,41,59)" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke={rate >= 95 ? "#10B981" : rate >= 80 ? "#F59E0B" : "#EF4444"} strokeWidth="3" strokeDasharray={`${rate} ${100 - rate}`} strokeLinecap="round" />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-300">{rate}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Email Delivery Log (Paginated + Filterable) ───
function EmailDeliveryLog() {
  const confirm = useConfirm();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEvent, setFilterEvent] = useState("");
  const [search, setSearch] = useState("");
  const [retrying, setRetrying] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);

  const searchRef = useRef(search);
  searchRef.current = search;

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 25 });
      if (filterStatus && filterStatus !== "all") params.set("status", filterStatus);
      if (filterEvent && filterEvent !== "all") params.set("event_type", filterEvent);
      if (searchRef.current) params.set("search", searchRef.current);
      const r = await API.get(`/admin/email/monitoring/logs?${params}`);
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
      setPage(r.data.page || 1);
      setTotalPages(r.data.total_pages || 1);
    } catch { toast.error("Failed to load logs"); }
    finally { setLoading(false); }
  }, [filterStatus, filterEvent]);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  const handleSearch = () => fetchLogs(1);
  const handleClearAll = async () => {
    if (!(await confirm({ title: "Clear Email Logs", description: "Clear all email delivery logs? This cannot be undone.", confirmText: "Clear All Logs", variant: "destructive" }))) return;
    try {
      await API.delete("/admin/smtp/logs");
      setLogs([]); setTotal(0); setPage(1); setTotalPages(1);
      toast.success("All logs cleared");
    } catch { toast.error("Failed to clear logs"); }
  };

  const retryEmail = async (logId) => {
    setRetrying(logId);
    try {
      const r = await API.post(`/admin/email/retry/${logId}`);
      if (r.data.success) {
        toast.success("Retry sent successfully");
      } else {
        toast.error("Retry failed — check configuration");
      }
      fetchLogs(page);
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
    finally { setRetrying(null); }
  };

  const EVENT_LABELS = {
    approval_status_change: "Approval", project_assignment: "Assignment", proposal_issued: "Proposal",
    user_invited: "Invitation", timesheet_submitted: "Timesheet", timesheet_approved: "Timesheet Review",
    password_reset: "Password Reset", test: "Test", unknown: "Other"
  };

  return (
    <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="email-delivery-log">
      <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-blue-400" />Delivery Log
            <span className="text-[10px] font-normal text-slate-500 ml-1">({total} total)</span>
          </CardTitle>
          <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
            {/* Status filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[120px] h-8 text-xs bg-slate-800 border-slate-700" data-testid="log-filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            {/* Event filter */}
            <Select value={filterEvent} onValueChange={setFilterEvent}>
              <SelectTrigger className="w-[140px] h-8 text-xs bg-slate-800 border-slate-700" data-testid="log-filter-event">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="approval_status_change">Approvals</SelectItem>
                <SelectItem value="project_assignment">Assignments</SelectItem>
                <SelectItem value="proposal_issued">Proposals</SelectItem>
                <SelectItem value="user_invited">Invitations</SelectItem>
                <SelectItem value="password_reset">Password Reset</SelectItem>
                <SelectItem value="test">Test</SelectItem>
              </SelectContent>
            </Select>
            {/* Search */}
            <div className="flex gap-1">
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search recipient or subject..." className="h-8 w-[200px] text-xs bg-slate-800 border-slate-700" data-testid="log-search-input"
                onKeyDown={e => e.key === "Enter" && handleSearch()} />
              <Button variant="ghost" size="sm" className="h-8 px-2" onClick={handleSearch} data-testid="log-search-btn"><SearchIcon className="w-3.5 h-3.5" /></Button>
            </div>
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => fetchLogs(page)} data-testid="refresh-logs-btn"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
            {total > 0 && <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-400 hover:text-rose-400" onClick={handleClearAll} data-testid="clear-all-logs-btn"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm animate-pulse">Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-slate-600 text-sm">No email logs match your filters.</div>
        ) : (
          <div className="table-scroll-wrapper">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 px-4 w-20">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 px-4">Recipient</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 px-4 w-24">Event</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 px-4">Subject</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 px-4 w-20">Provider</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 px-4 w-36">Timestamp</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 px-4 w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, i) => (
                  <>
                    <TableRow key={log.id || i} className={`border-slate-800/50 group cursor-pointer hover:bg-slate-800/30 transition-colors ${expandedLog === log.id ? "bg-slate-800/20" : ""}`} onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)} data-testid={`log-row-${i}`}>
                      <TableCell className="px-4 py-2.5">
                        {log.status === "sent"
                          ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400"><CheckCircle2 className="w-3 h-3" />Sent</span>
                          : <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-400"><XCircle className="w-3 h-3" />Failed</span>
                        }
                        {log.retried && <span className="ml-1 text-[9px] text-sky-400">(retried)</span>}
                        {log.retry_of && <span className="ml-1 text-[9px] text-violet-400">(retry)</span>}
                      </TableCell>
                      <TableCell className="px-4 py-2.5 text-sm font-mono text-slate-300 truncate max-w-[180px]">{log.to}</TableCell>
                      <TableCell className="px-4 py-2.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{EVENT_LABELS[log.event_type] || log.event_type}</span></TableCell>
                      <TableCell className="px-4 py-2.5 text-xs text-slate-400 max-w-[200px] truncate">{log.subject}</TableCell>
                      <TableCell className="px-4 py-2.5 text-[10px] text-slate-500 uppercase">{log.provider || "—"}</TableCell>
                      <TableCell className="px-4 py-2.5 text-[10px] font-mono text-slate-500">{log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}</TableCell>
                      <TableCell className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {log.status === "failed" && !log.retried && (
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 px-2"
                              disabled={retrying === log.id} onClick={() => retryEmail(log.id)} data-testid={`retry-btn-${i}`}>
                              <RefreshCw className={`w-3 h-3 mr-1 ${retrying === log.id ? "animate-spin" : ""}`} />{retrying === log.id ? "..." : "Retry"}
                            </Button>
                          )}
                          <button className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-rose-500/10 text-slate-500 hover:text-rose-400 transition-all" data-testid={`delete-log-${i}`}
                            onClick={async () => {
                              try { await API.delete(`/admin/smtp/logs/${log.id}`); fetchLogs(page); } catch { toast.error("Failed to delete"); }
                            }}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedLog === log.id && (
                      <TableRow key={`${log.id}-detail`} className="border-slate-800/50">
                        <TableCell colSpan={7} className="px-4 py-3 bg-slate-800/20">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                            <div><span className="text-[10px] text-slate-500 uppercase block">Log ID</span><span className="font-mono text-slate-400 text-[11px]">{log.id}</span></div>
                            <div><span className="text-[10px] text-slate-500 uppercase block">Provider</span><span className="text-slate-300">{log.provider || "N/A"}</span></div>
                            <div><span className="text-[10px] text-slate-500 uppercase block">Retry Count</span><span className="text-slate-300">{log.retry_count || 0}</span></div>
                            {log.retry_of && <div><span className="text-[10px] text-slate-500 uppercase block">Retry Of</span><span className="font-mono text-violet-400 text-[11px]">{log.retry_of}</span></div>}
                            {log.error && <div className="col-span-2 md:col-span-4"><span className="text-[10px] text-slate-500 uppercase block">Error Details</span><span className="text-rose-400 text-[11px] break-all">{log.error}</span></div>}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
            <span className="text-xs text-slate-500">Page {page} of {totalPages} ({total} records)</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => fetchLogs(page - 1)} data-testid="log-page-prev">
                <ChevronLeftIcon className="w-3 h-3 mr-0.5" />Prev
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page + i - 2;
                if (p < 1 || p > totalPages) return null;
                return (
                  <Button key={p} variant={p === page ? "default" : "ghost"} size="sm" className={`h-7 w-7 text-xs ${p === page ? "bg-blue-600 text-white" : ""}`} onClick={() => fetchLogs(p)} data-testid={`log-page-${p}`}>
                    {p}
                  </Button>
                );
              })}
              <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => fetchLogs(page + 1)} data-testid="log-page-next">
                Next<ChevronRightIcon className="w-3 h-3 ml-0.5" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}



function TechnicalSystemsTab() {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newGroup, setNewGroup] = useState("Mechanical");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/admin/technical-systems").then(r => setSystems(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const groups = ["Mechanical", "Electrical", "Public Health"];

  const addSystem = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const maxOrder = Math.max(0, ...systems.map(s => s.order || 0));
      const r = await API.post("/admin/technical-systems", { name: newName.trim(), group: newGroup, order: maxOrder + 1 });
      setSystems(prev => [...prev, r.data]);
      setNewName("");
      toast.success("System added");
    } catch { toast.error("Failed to add"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (sys) => {
    try {
      await API.put(`/admin/technical-systems/${sys.id}`, { active: !sys.active });
      setSystems(prev => prev.map(s => s.id === sys.id ? { ...s, active: !s.active } : s));
    } catch { toast.error("Failed to update"); }
  };

  const deleteSystem = async (id) => {
    try {
      await API.delete(`/admin/technical-systems/${id}`);
      setSystems(prev => prev.filter(s => s.id !== id));
      toast.success("System removed");
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="animate-pulse h-40 bg-slate-800 rounded-lg" />;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="admin-tech-systems">
      <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
        <CardTitle className="text-sm font-semibold">Technical Systems Library</CardTitle>
        <p className="text-xs text-slate-500 mt-0.5">Manage the list of MEP technical systems available for project selection.</p>
      </CardHeader>
      <CardContent className="p-4 space-y-6">
        {/* Add new */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">System Name</Label>
            <Input value={newName} onChange={e => setNewName(e.target.value)} className="mt-1 bg-slate-800 border-slate-700 h-8 text-sm" placeholder="e.g. Underfloor Heating" data-testid="new-tech-system-name" onKeyDown={e => e.key === "Enter" && addSystem()} />
          </div>
          <div className="w-40">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Group</Label>
            <Select value={newGroup} onValueChange={setNewGroup}>
              <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 h-8 text-xs" data-testid="new-tech-system-group"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {groups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={addSystem} disabled={saving || !newName.trim()} className="bg-blue-600 hover:bg-blue-500 h-8 text-xs" data-testid="add-tech-system-btn">Add</Button>
        </div>

        {/* Grouped lists */}
        {groups.map(group => {
          const groupSystems = systems.filter(s => s.group === group).sort((a, b) => (a.order || 0) - (b.order || 0));
          return (
            <div key={group}>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{group} ({groupSystems.length})</h3>
              <div className="space-y-1">
                {groupSystems.map(sys => (
                  <div key={sys.id} className={`flex items-center justify-between py-1.5 px-3 rounded-md border ${sys.active ? "bg-slate-800/30 border-slate-800" : "bg-slate-900/50 border-slate-800/50 opacity-50"}`}>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleActive(sys)} className={`w-3 h-3 rounded-full border-2 transition-colors ${sys.active ? "bg-emerald-500 border-emerald-500" : "border-slate-600 bg-transparent"}`} data-testid={`toggle-tech-${sys.id}`} />
                      <span className={`text-sm ${sys.active ? "text-slate-200" : "text-slate-500 line-through"}`}>{sys.name}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteSystem(sys.id)} className="h-5 w-5 p-0 text-slate-600 hover:text-rose-400"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                {groupSystems.length === 0 && <p className="text-xs text-slate-600 pl-3">No systems in this group</p>}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}